/**
 * Pure reducer for the chat / agent-communication state in Foundry's
 * Workspace. Replaces ~300 lines of inline `useState` + chunk-handler +
 * effect code in `pages/Workspace.tsx`.
 *
 * Why this exists:
 *
 *   The agent-communication layer has had a parade of state desyncs:
 *
 *     - alpha.41: queue messages typed during in-flight turns
 *       (#164). Required `isAiTyping` to be the source of truth for
 *       "agent is busy".
 *     - alpha.42: persist SDK session id across restarts (#168) —
 *       in-memory state lost on Foundry restart.
 *     - alpha.43: terminal vs non-terminal error chunks (#169) — the
 *       renderer was flipping `isAiTyping=false` on every error
 *       chunk, including informational SDK errors mid-turn.
 *     - alpha.44: AskUserQuestion chat-message fallback (#170) — the
 *       option-button card was firing both the SDK gate AND a chat
 *       message, racing the still-active turn into the bouncer.
 *
 *   Each of those was a real-user-reported regression that slipped
 *   past existing tests because the renderer's interaction layer
 *   was untested. The current test surface (`__tests__/agent-adapter`)
 *   covers main-side translateSdkMessage + session lifecycle, but
 *   nothing covers the renderer's chunk handler, queue, drain
 *   effect, AskUserQuestion dispatch, or Stop button.
 *
 *   This reducer is the testable home for that logic. Workspace.tsx
 *   becomes a thin shell: state lives in `useReducer(reduceChatState,
 *   ...)`; effects returned from the reducer drive IPC calls; UI
 *   reads from the resulting state. Every desync we've shipped — and
 *   future ones we haven't — can be pinned by a table-driven test on
 *   this file.
 *
 * Design notes:
 *
 *   - **Pure**. The reducer NEVER fires IPC, dispatches CustomEvents,
 *     or calls `setState`. It returns a `{ state, effects }` tuple.
 *     The caller (a thin hook in Workspace) interprets the effects.
 *     This is what makes the tests trivial.
 *
 *   - **Effects, not commands**. Effects describe what to do; the
 *     interpreter does it. Lets us swap real IPCs for mocks in tests
 *     without touching reducer logic.
 *
 *   - **No setState-in-reducer**. React's reducer convention; the
 *     reducer is a pure (state, action) → state function.
 *
 *   - **One reducer for the lot**. Could split queue / messages /
 *     typing into separate reducers, but cross-state invariants
 *     (e.g. "Stop clears the queue AND fires interrupt") are easier
 *     to encode + test in a single state object.
 */

import {
  INITIAL_PROMPT_MESSAGE_ID_PREFIX,
  type AgentSendAttachment,
  type AgentStreamChunk,
  type DependencyRequestPush,
  type TemplateFileOverrideRequestPush,
  type InlineCheckpoint,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { SerialisedError } from '@volley/crucible-ipc-types/error-types';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import { denormaliseCheckpointContent } from '@volley/crucible-ipc-types/checkpoint-content';
import { redactSecrets } from '@volley/crucible-ipc-types/redact-secrets';
import { stripActivityMarkers } from './strip-activity-markers.js';
import { findSupersededTimelineRows } from './turn-dedupe.js';
import type { ChatMessage, MessageBlock, ToolCallEntry } from './types.js';
import { compareJournalOrderKeys, type JournalOrderKey } from './journal-order-key.js';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/**
 * Per-agent row built up from `agent_started` / `agent_stopped`
 * chunks plus the `text` / `tool_call` / `tool_result` / `warning`
 * chunks tagged with the agent's id (Phase 1 of the Agents-panel
 * plan, plan doc: `docs/plan-big-work-and-agents-ui.md`). Until
 * alpha.49 paints the UI this state is built but not rendered —
 * Phase 1 ships the data substrate.
 */
export interface AgentRow {
  readonly agentId: string;
  readonly agentType: string;
  readonly status: 'running' | 'success' | 'interrupted' | 'error';
  /** Capped at MAX_RECENT_CHUNKS_PER_AGENT to bound memory; alpha.49's
   *  panel virtualises the scrollable list, so we can lift this then. */
  readonly recentChunks: readonly AgentStreamChunk[];
  /**
   * Monotonic counter of chunks ever appended to this row, including
   * those that have since been trimmed off the front of
   * `recentChunks` when it exceeded the cap. The renderer keys
   * mini-transcript items on `chunksSeenTotal - recentChunks.length + i`
   * so each chunk gets a stable key that survives front-trim shifts —
   * critical for React reconciliation when a long-running agent
   * crosses the cap mid-render. (Reviewer catch on Phase 2 PR:
   * index keys break the moment trim slides indices.)
   */
  readonly chunksSeenTotal: number;
  readonly parentAgentId?: string;
  readonly startedAt: number;
  readonly stoppedAt?: number;
}

/** Cap on per-agent retained chunks. 200 is enough that the
 *  scrollable mini-transcript shows real history without memory
 *  growing unboundedly on a long fan-out. Older chunks get
 *  shifted out when the cap fills. */
export const MAX_RECENT_CHUNKS_PER_AGENT = 200;

/**
 * Synthetic "main agent" row state — issue #202 / Layer 2 of the
 * always-visible-Agents-panel structural fix.
 *
 * The orchestrator (the SDK turn running on the main thread — does
 * the `/build-game` interview, dispatches specialists, synthesises
 * results) NEVER fires a `SubagentStart` hook for itself. Pre-#202,
 * that meant the right-column Agents panel stayed empty during
 * Phase A interview and only populated once specialist subagents
 * fanned out in Phase B. Users had no signal that anything was
 * happening for the first chunk of every turn.
 *
 * This slice tracks the main thread as if it were a subagent, but
 * driven by the ABSENCE of `agentId` on incoming chunks rather than
 * a `SubagentStart` hook. `recentChunks` mirrors the per-agent cap
 * (`MAX_RECENT_CHUNKS_PER_AGENT`) so memory bounds match
 * specialists. `chunksSeenTotal` lets the renderer derive stable
 * post-trim keys, same as `AgentRow`.
 *
 * Status state machine (computed at chunk-receive time; isAiTyping
 * is the source-of-truth flag):
 *   - `idle`     — !isAiTyping (turn over, no work in flight).
 *   - `working`  - isAiTyping AND most-recent chunk in
 *                  recentChunks is a tool_call or tool_result.
 *   - `thinking` - isAiTyping AND no recent tool calls (assistant
 *                  is generating text or thinking pre-tool).
 *
 * Reset to null at the start of every new turn (`startTurn`) so each
 * turn's main-agent activity is independent. Reset to null on
 * `game/changed` (cross-game leak guard).
 */
export interface MainAgentRow {
  readonly status: 'idle' | 'thinking' | 'working';
  /** Capped at MAX_RECENT_CHUNKS_PER_AGENT — same memory bound as
   *  specialist `AgentRow.recentChunks`. */
  readonly recentChunks: readonly AgentStreamChunk[];
  /** Monotonic counter of chunks ever appended, including those
   *  that have since been trimmed off the front. The renderer
   *  derives stable insertion-order keys as
   *  `chunksSeenTotal - recentChunks.length + i`. */
  readonly chunksSeenTotal: number;
}

/**
 * A message queued while a turn was in flight (#2799). `text` has any
 * selection-context blocks already baked in (the `user/send` branch
 * folds them before queueing, same as the direct-send path), and
 * `attachments` ride alongside so an image-bearing send survives the
 * wait and fires intact at the next turn boundary.
 */
export interface QueuedChatMessage {
  readonly text: string;
  readonly attachments: readonly AgentSendAttachment[];
}

/**
 * Hard cap on `queuedMessages` (#2799). Mid-turn sends beyond this
 * are refused with a `ui/error` — an unbounded queue lets a user
 * stack an hour of stale instructions behind a long build turn.
 */
export const MAX_QUEUED_MESSAGES = 10;

export interface ChatState {
  /** Persisted message timeline, oldest first. Mutated in-place by the
   *  chunk handler (text streamed onto the latest assistant bubble,
   *  tool calls / results upserted onto the active assistant message). */
  readonly messages: ChatMessage[];
  /** Current value of the chat input box. */
  readonly chatInput: string;
  /** True when an agent turn is in flight from the renderer's POV.
   *  Source of truth for the queue's "should I bypass IPC?" check. */
  readonly isAiTyping: boolean;
  /** Id of the assistant message that's currently streaming, or null
   *  when no turn is in flight. Subsequent text/tool chunks find their
   *  target by this id. */
  readonly currentAiMsgId: string | null;
  /**
   * #3021 — TRANSIENT in-progress preview of the model's live output. Streamed
   * `preview` deltas accumulate here (text + reasoning, in emit order); it is
   * NEVER appended to a message's `content` / `blocks` and never persisted. The
   * complete assistant message is the sole authoritative writer — its
   * `text` / `thinking` / `tool_call` chunks REPLACE this (clear it), and every
   * terminal-reset seam nulls it out. `null` when no preview is in flight.
   * Painting pairs with the separate PR2 GUI status-row work; this field is the
   * data substrate the reducer maintains.
   */
  readonly livePreview: string | null;
  /** Per-game send queue. Filled when `user:send` fires while
   *  `isAiTyping=true`. Drained one entry at a time when `chunk:done`
   *  / `chunk:interrupted` arrives. Reset on `game:changed`.
   *  Capped at MAX_QUEUED_MESSAGES (#2799); entries carry their
   *  attachments so an image-bearing send queues intact mid-turn
   *  instead of being refused. */
  readonly queuedMessages: readonly QueuedChatMessage[];
  /** Active + recently-finished subagents, keyed by `agentId`.
   *  Populated from `agent_started` / `agent_stopped` chunks +
   *  agentId-tagged passthrough chunks. Cleared at the start of
   *  every new turn so this turn's agents don't bleed into the
   *  next. Read by the renderer's right-column Agents panel
   *  (alpha.49+). */
  readonly agents: ReadonlyMap<string, AgentRow>;
  /**
   * Synthetic main-agent (orchestrator) row — issue #202.
   *
   * The orchestrator runs on the main thread and never fires its
   * own `SubagentStart` hook, so it doesn't appear in the `agents`
   * map. This slice mirrors what `agents` does for specialists:
   * captures un-tagged (`!chunk.agentId`) `text` / `tool_call` /
   * `tool_result` / `warning` chunks and tracks status off the
   * `isAiTyping` flag plus the recent-tool-call shape.
   *
   * `null` when the panel has nothing to show for the main thread:
   * before the first un-tagged chunk of a turn arrives, after a
   * `startTurn` reset, after `game/changed`. The Agents panel
   * renders this row (when non-null) at the TOP of the list,
   * ABOVE all specialists, with a distinct icon + label so it's
   * visually clear which is the orchestrator.
   */
  readonly mainAgent: MainAgentRow | null;
  /**
   * Screenshot-paste MVP (#193) — images the user has pasted into the
   * input but not yet sent. Cleared by `user/send` (consumed) and by
   * `game/changed` (cross-game nav). NOT persisted; if the renderer
   * unmounts mid-compose the chips are gone, by design — see #193 for
   * the persistence design pass.
   */
  readonly pendingAttachments: readonly AgentSendAttachment[];
  /**
   * Select-to-chat P1 (`docs/design/preview-select-to-chat-prd.md`
   * §5.5) — per-attachment formatted `crucible-selection` context
   * blocks, INDEX-ALIGNED with `pendingAttachments` (`null` for plain
   * paste/file attachments). Kept as a parallel array rather than a
   * wrapper type so the `AgentSendAttachment` wire/chip contract is
   * untouched; the alignment invariant is enforced by the only four
   * writers (`user/add-attachment`, `user/remove-attachment`,
   * `startTurn`, and the `user/send` mid-turn queue branch, which
   * consumes both arrays into the queued entry — #2799) and pinned
   * in `state.test.ts`.
   *
   * At send time the non-null blocks are prepended to the message
   * text (the agent reads them inline); they never ride the
   * attachments IPC field.
   */
  readonly pendingSelectionContexts: readonly (string | null)[];
  /**
   * Phase 3 visibility — last `sendMessage`'s SDK-reported cost +
   * turn count, captured from the `done` (or terminal `error`)
   * chunk's `totalCostUsd` / `numTurns` fields. Reset to `null` at
   * the start of every new turn so the strip clears between turns.
   * Cost surfaced only when `showCostInChat` is enabled in
   * `coreConfig`; turn count is always shown.
   */
  readonly lastTurnSpend: { totalCostUsd?: number; numTurns?: number } | null;
  /**
   * Crucible #548 — queue of pending `requestDependency` approval
   * modals. The renderer surfaces the FIRST entry; subsequent
   * concurrent requests stack behind it. The reducer is the sole
   * writer; `deps/request` appends, `deps/respond` removes the
   * matching entry by sessionId.
   *
   * Modelled as a queue rather than a singleton so the UX doesn't
   * silently drop a second request that arrives while the modal is
   * open — every approved/declined session is acknowledged
   * deterministically and the user works through them one at a time.
   */
  readonly pendingDependencyRequests: readonly DependencyRequestPush[];
  /**
   * Crucible #3491 — queue of pending `requestTemplateFileOverride`
   * approval modals. Same queue-not-singleton shape as
   * `pendingDependencyRequests`, for the same reason (never silently
   * drop a second concurrent request).
   */
  readonly pendingTemplateFileOverrideRequests: readonly TemplateFileOverrideRequestPush[];
  /** Game id this state belongs to. Used by `game:changed` to detect
   *  cross-game navigation (which resets the queue + messages). */
  readonly gameId: string;
  /**
   * Crucible #602 PR #C1 — per-message held-back marker fragments
   * for the agent-transparency activity-annotation strip
   * (`renderer/chat/strip-activity-markers.ts`).
   *
   * Keyed by the in-flight assistant `ChatMessage.id`; each value
   * carries the trailing partial-marker fragment and the SDK message
   * UUID that sourced it. The next text chunk for the same message
   * prepends this fragment before re-running the strip — that's what
   * makes a marker split across two chunks succeed while allowing a
   * refusal retraction to remove only its own held fragment.
   *
   * Cleared on the message's terminal chunk (`done` / `interrupted`
   * / `error` / out-of-band `agent_stopped`-for-orchestrator) by
   * flushing AS-IS into `message.content` — by then no further
   * chunks can complete the marker, so a hanging fragment is
   * treated as accidental literal text. Wiped wholesale on
   * `chat/loaded` and `game/changed`.
   *
   * Reducer purity invariant: this is a plain immutable map (NOT a
   * `MarkerStripBuffer` class instance) so the reducer stays pure
   * — `pendingMarkers` is rebuilt on every text chunk via spread.
   */
  readonly pendingMarkers: ReadonlyMap<string, PendingActivityMarker>;
  /**
   * GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — honest hydration
   * state machine. Distinguishes the three states the chat surface
   * could previously not tell apart:
   *
   *   - `'loading'` — the per-game `chat:load` is in flight (or being
   *     retried). On the cloud path the first touch can block on a git
   *     clone for up to ~120s, so this is NOT a transient flicker.
   *   - `'ready'`   — `chat/loaded` applied; the message timeline is
   *     authoritative (even when empty — an empty READY game is the
   *     fresh-game first-pitch state, not a blank-on-error).
   *   - `'error'`   — hydration failed. Before GAP-3 a transient
   *     transport error left `messages` empty + a banner, rendering an
   *     indistinguishable permanent blank with no retry.
   *
   * Initial value is `'loading'`: a freshly-constructed state is always
   * mid-hydration (the mount effect runs `chat:load` immediately), and
   * `game/changed` resets back to `'loading'` for the new game.
   */
  readonly hydrationStatus: 'loading' | 'ready' | 'error';
  /**
   * GAP-3 — the failure that put `hydrationStatus` into `'error'`, or
   * `null` otherwise. Carries `retryable` so the surface can decide
   * whether to offer a retry action: retryable transport errors get a
   * "Try again" affordance, while corrupt-history (CrucibleError
   * category `'state'`, `retryable: false`) keeps the existing one-shot
   * recovery banner via `chatPersistError` and does NOT auto-retry.
   * Cleared on `chat/loaded` and `game/changed`.
   */
  readonly hydrationError: SerialisedError | null;
}

export interface PendingActivityMarker {
  readonly text: string;
  readonly sourceMessageUuid?: string;
}

export const initialChatState = (gameId: string): ChatState => ({
  messages: [],
  chatInput: '',
  isAiTyping: false,
  currentAiMsgId: null,
  // #3021 — no transient preview in flight at init.
  livePreview: null,
  queuedMessages: [],
  agents: new Map(),
  // #202: no main-agent row until the first un-tagged chunk of a
  // turn arrives. Reset to null at startTurn / game/changed so each
  // turn's activity is independent and a stale row from a previous
  // turn or game can't bleed in.
  mainAgent: null,
  pendingAttachments: [],
  pendingSelectionContexts: [],
  lastTurnSpend: null,
  pendingDependencyRequests: [],
  pendingTemplateFileOverrideRequests: [],
  gameId,
  // Crucible #602 PR #C1 — no held-back marker fragments at init.
  // Populated only when a text chunk's trailing bytes look like the
  // start of an activity marker that hasn't closed yet.
  pendingMarkers: new Map(),
  // GAP-3 (gui-parity-plan.md Phase 2) — a fresh state is always
  // mid-hydration: the Workspace mount effect kicks off `chat:load`
  // immediately and only `chat/loaded` (ready) or `chat/hydration-failed`
  // (error) move us off `'loading'`.
  hydrationStatus: 'loading',
  hydrationError: null,
});

// ---------------------------------------------------------------------------
// Actions — every input that can mutate state
// ---------------------------------------------------------------------------

export type ChatAction =
  /** Initial chat-history load completed. Replaces the `messages` array. */
  | { type: 'chat/loaded'; messages: ChatMessage[]; agentInFlight?: boolean }
  /**
   * Explicitly begin the persisted initial prompt after the caller has proved
   * the durable interview state is still an untouched create seed.
   */
  | { type: 'chat/autofire-initial-prompt'; now: number }
  /**
   * Reattach poll (cloud-furnace 2026-06-22) — incremental `agent.history`
   * projection MERGED into the timeline while a build runs server-side and
   * this renderer is NOT receiving the live stream (e.g. after a reconnect
   * / on a second device). Unlike `chat/loaded` it does NOT replace the
   * array, touch `hydrationStatus`, re-fire the initial prompt, or flip any
   * cursor — it only appends messages whose id isn't already present, so a
   * locally-streaming message (in `messages` keyed by `currentAiMsgId`) is
   * never clobbered. Merge keeps existing entries on id collision.
   * When `authoritative` is true, a cold/full reconcile drops absent
   * `agent-stream` rows while preserving renderer placeholders, the current
   * streaming row, and error/checkpoint rows; warm merges stay append/update-only.
   */
  | { type: 'chat/agent-history-merged'; messages: ChatMessage[]; authoritative?: boolean }
  /**
   * Reattach reconcile (cloud-furnace 2026-06-22) — authoritative
   * server-side build liveness from `agent:get-status`, used by the
   * reattach poll. The load-bearing case is `inFlight:false`: when the
   * build has finished server-side but this (non-originating) renderer
   * never received the terminal `chunk:done`, `isAiTyping` stays true and
   * the UI shows "working" forever. This clears it (idle). Only acts when
   * the renderer is NOT mid-streaming a local message AND `inFlight` is
   * false — so it can't race a live originator stream. `inFlight:true` is
   * a no-op here (hydration already sets busy state).
   */
  | { type: 'chat/agent-flight-reconciled'; inFlight: boolean }
  /**
   * GAP-3 (gui-parity-plan.md Phase 2) — the per-game hydration load
   * failed terminally (the mount effect has exhausted its bounded retry
   * budget for a retryable transport error, OR hit a non-retryable error
   * such as corrupt-history that must not be retried). Moves
   * `hydrationStatus` to `'error'` and stashes the `SerialisedError` so
   * the chat surface can render a legible error state instead of a blank
   * panel. `retryable` is carried through on the error so the surface can
   * decide whether to show a "Try again" affordance.
   */
  | { type: 'chat/hydration-failed'; error: SerialisedError }
  /**
   * GAP-3 (gui-parity-plan.md Phase 2) — flip hydration back to
   * `'loading'` without touching the message timeline. Dispatched by the
   * mount effect when a user-driven "Try again" re-runs the load from an
   * `'error'` state, so the surface shows the loading affordance again
   * instead of staying frozen on the error. Distinct from `game/changed`
   * (which is a full per-game reset that also wipes `chatInput` + queue):
   * a retry of the SAME game must not discard a draft the user typed.
   */
  | { type: 'chat/hydration-retrying' }
  /** Renderer-authored status event inserted into the chat timeline. */
  | { type: 'chat/local-event'; id: string; content: string; timestamp: number }
  /** User typed in the input box. */
  | { type: 'user/type'; text: string }
  /** User clicked Send. Reducer decides queue vs direct dispatch. */
  | { type: 'user/send' }
  /** Programmatic text-only send. Used by CTAs that should bypass the composer draft. */
  | { type: 'user/send-text'; text: string }
  /** User clicked the × on a queued message chip. */
  | { type: 'user/unqueue'; index: number }
  /**
   * Issue #369 — user clicked the "Try again" button on a chat-bubble
   * error whose `retryable: true` was surfaced by PR #368. The reducer
   * finds the user-role message immediately preceding `messageId` (the
   * input the bouncer / SDK rejected), clears the error bubble from
   * the timeline, and emits a fresh `ipc/agent-send` carrying that
   * text. No-op if a turn is currently in flight (`isAiTyping=true`)
   * or if the preceding user message can't be found — defensive
   * against double-clicks that race the bouncer's 1.5s grace timer.
   */
  | { type: 'user/retry'; messageId: string }
  /** User clicked Stop. Clears queue + emits interrupt effect. */
  | { type: 'user/stop' }
  /** User answered an AskUserQuestion option. Emits gate IPC, NOT chat send. */
  | {
      type: 'user/answer-question';
      toolUseID: string;
      questionText: string;
      answer: string;
    }
  /** Screenshot-paste MVP (#193) — user pasted an image into the
   *  input. Reducer enforces per-image / per-message caps and surfaces
   *  an error toast effect when they're exceeded; otherwise the
   *  attachment is appended to `pendingAttachments`. */
  | {
      type: 'user/add-attachment';
      attachment: AgentSendAttachment;
      /** Select-to-chat P1 — formatted `crucible-selection` block for
       *  preview-selection attachments; absent for plain pastes. */
      selectionContext?: string;
    }
  /** User clicked × on a pasted-image chip. */
  | { type: 'user/remove-attachment'; index: number }
  /** A chunk arrived on the agent:stream channel. Handles all variants. */
  | { type: 'chunk/received'; chunk: AgentStreamChunk }
  /** sendChat's IPC promise rejected (the rare main-side throw). */
  | { type: 'ipc/send-failed'; assistantMsgId: string; error: SerialisedError }
  /** The authoritative runtime declined a raced automatic create kickoff. */
  | { type: 'ipc/autofire-not-needed'; assistantMsgId: string }
  /** Workspace navigated to a different game. Resets queue + messages. */
  | { type: 'game/changed'; gameId: string }
  /**
   * Crucible #548 — main pushed a `requestDependency` approval modal.
   * Reducer enqueues it onto `pendingDependencyRequests`. No effect:
   * the modal lives entirely in renderer state until the user
   * answers.
   */
  | {
      type: 'deps/request';
      request: DependencyRequestPush;
    }
  /**
   * Crucible #548 — user clicked Install (with or without a version
   * pin) on the approval modal. Reducer removes the entry from the
   * queue and emits an `ipc/deps-respond` effect carrying the
   * approved version. Main then drives the install; the modal is
   * already gone from the UI by the time pnpm finishes.
   */
  | {
      type: 'deps/approve';
      sessionId: string;
      finalVersion: string;
    }
  /**
   * Crucible #548 — user clicked Decline (or dismissed via Esc /
   * Back-button) on the approval modal. Reducer removes the entry
   * and emits an `ipc/deps-respond` effect with `approved: false` +
   * a short reason the agent reads back in the tool result.
   */
  | {
      type: 'deps/decline';
      sessionId: string;
      reason: string;
    }
  /**
   * Crucible #3491 — main pushed a `requestTemplateFileOverride`
   * approval modal. Reducer enqueues it onto
   * `pendingTemplateFileOverrideRequests`. No effect: the modal lives
   * entirely in renderer state until the user answers.
   */
  | {
      type: 'template-file-override/request';
      request: TemplateFileOverrideRequestPush;
    }
  /**
   * Crucible #3491 — user clicked Delete file on the approval modal.
   * Reducer removes the entry from the queue and emits an
   * `ipc/template-file-override-respond` effect.
   */
  | {
      type: 'template-file-override/approve';
      sessionId: string;
    }
  /**
   * Crucible #3491 — user clicked Decline (or dismissed via Esc /
   * Back-button) on the approval modal.
   */
  | {
      type: 'template-file-override/decline';
      sessionId: string;
      reason: string;
    }
  /**
   * Crucible #602 PR #C4 — inline-checkpoint arrived on the
   * `activity:checkpoint` IPC push. Reducer synthesises an
   * `inline-checkpoint` `ChatMessage` and splices it into
   * `messages` at the right timestamp index.
   *
   * Pure data transform; no effects. Dispatched by the Workspace
   * subscription (Issue E) which is itself flag-gated on
   * `CRUCIBLE_AGENT_TRANSPARENCY`. The reducer does NOT re-check
   * the flag — by design (cheap, correct: if the action arrives,
   * the dispatcher already decided this game opted in).
   *
   * See `docs/c4-inline-checkpoints-design.md` §9 "Reducer action".
   */
  | {
      type: 'chat/checkpoint-received';
      gameId: string;
      checkpoint: InlineCheckpoint;
    };

// ---------------------------------------------------------------------------
// Effects — side-effect intents the interpreter executes
// ---------------------------------------------------------------------------

export type ChatEffect =
  /** Fire the `agent:send` IPC. The interpreter wraps this in a
   *  catch that dispatches `ipc/send-failed`.
   *
   *  `attachments` is set when the user pasted images alongside the
   *  text — the main-side adapter switches to the SDK's streaming-input
   *  iterable form so they ride through as image content blocks
   *  (#193). */
  | {
      type: 'ipc/agent-send';
      gameId: string;
      message: string;
      assistantMsgId: string;
      userMessageId?: string;
      assistantMessageId?: string;
      userTimestamp?: number;
      assistantTimestamp?: number;
      turnId?: string;
      initialAutofire?: true;
      attachments?: readonly AgentSendAttachment[];
    }
  /** Fire the `agent:interrupt` IPC. */
  | { type: 'ipc/agent-interrupt'; gameId: string }
  /** Fire the `agent:answer-user-question` IPC (gate path). */
  | {
      type: 'ipc/agent-answer';
      gameId: string;
      toolUseID: string;
      answers: Record<string, string>;
    }
  /** Surface a transient client-side error to the user (e.g. attachment
   *  cap exceeded). The interpreter renders this however it wants —
   *  current implementation marks the active assistant placeholder, but
   *  callers without one can show a toast. */
  | { type: 'ui/error'; message: string }
  /**
   * Crucible #548 — fire the `deps:respond` IPC. Carries the user's
   * approval decision plus (on approve) the final version they
   * authorised.
   *
   * #3059 — the effect also carries the ORIGINAL `request`. The reducer
   * pops the modal in the same reduction that emits this effect, so on
   * the cloud path a rejected IPC (WSS drop / pod roll mid-flight) would
   * otherwise strand the agent's held-open gate with zero user signal.
   * The interpreter's catch re-enqueues `request` so the modal reappears
   * and the user can retry, instead of silently proceeding.
   */
  | {
      type: 'ipc/deps-respond';
      sessionId: string;
      approved: true;
      finalVersion: string;
      request: DependencyRequestPush;
    }
  | {
      type: 'ipc/deps-respond';
      sessionId: string;
      approved: false;
      reason: string;
      request: DependencyRequestPush;
    }
  /**
   * Crucible #3491 — fire the `template-file-override:respond` IPC.
   * Same re-enqueue-on-failure shape as `ipc/deps-respond` (#3059):
   * carries the ORIGINAL request so the interpreter's catch can
   * restore the modal if the IPC rejects, instead of stranding the
   * agent's held-open gate with zero user signal.
   */
  | {
      type: 'ipc/template-file-override-respond';
      sessionId: string;
      approved: true;
      request: TemplateFileOverrideRequestPush;
    }
  | {
      type: 'ipc/template-file-override-respond';
      sessionId: string;
      approved: false;
      reason: string;
      request: TemplateFileOverrideRequestPush;
    };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export interface ReducerResult {
  state: ChatState;
  effects: ChatEffect[];
}

/**
 * #3059 — should a FAILED deps:respond re-enqueue its request onto the modal
 * queue? Only when the request belongs to the currently-active workspace game.
 * A late rejection landing after the user navigated away (game A -> B) must NOT
 * re-surface game A's dependency modal on game B or route retries to the wrong
 * session (the cross-game class #3062/#3044 also guard). Mirrors the
 * `deps:request` push-drop idiom exactly (`if (gameId && push.gameId !== gameId)
 * return;`): a falsy active game does not block (there is no game to mismatch).
 */
export function shouldReEnqueueDependencyRequest(
  requestGameId: string,
  activeGameId: string | null | undefined,
): boolean {
  return !activeGameId || requestGameId === activeGameId;
}

/**
 * Crucible #3491 — same re-enqueue guard as
 * `shouldReEnqueueDependencyRequest`, for a failed
 * `template-file-override:respond` IPC call.
 */
export function shouldReEnqueueTemplateFileOverrideRequest(
  requestGameId: string,
  activeGameId: string | null | undefined,
): boolean {
  return !activeGameId || requestGameId === activeGameId;
}

/**
 * A fingerprint of everything that RENDERS for an agent message, used by the
 * reattach merge (`chat/agent-history-merged`) to decide whether an observed
 * turn actually advanced between polls. Must capture every in-place mutation a
 * streaming turn makes, NOT just structural growth — in particular a `tool.call`
 * filling in on a `tool.result` (status `running → success/error` + `output`)
 * changes neither `content` nor any array length, so a length-only check would
 * miss it and leave the turn frozen (the very bug this merge exists to fix).
 * Covers: terminal status, per-block kind + text length (text/thinking blocks
 * accrete in place), and per-tool status + output length.
 */
function agentMessageRenderKey(m: ChatMessage): string {
  const blocks = (m.blocks ?? [])
    .map((b) =>
      b.kind === 'text' || b.kind === 'thinking' ? `${b.kind}#${b.text.length}` : b.kind,
    )
    .join('|');
  const tools = (m.toolCalls ?? []).map((t) => `${t.status}#${t.output?.length ?? 0}`).join('|');
  return `${m.streamStatus ?? ''}${blocks}${tools}${m.content}`;
}

function sameRenderedAgentMessage(a: ChatMessage, b: ChatMessage): boolean {
  return agentMessageRenderKey(a) === agentMessageRenderKey(b);
}

/**
 * Merge the freshly-projected `toolCalls` over the existing ones for a reattach
 * update. The projection is authoritative for status/output/order, but it never
 * carries `policyKind` — the renderer-only deny classification the chat reducer
 * stamps from a `policy_decision` chunk. A shallow `{ ...existing, ...m }` would
 * swap the whole array and strip it, reverting a deny chip to default styling;
 * carry it forward per tool-call id. (`policyKind` is the only renderer-only
 * field on `ToolCallEntry`; everything else comes from the journal.)
 */
function mergeReattachToolCalls(
  existing: ToolCallEntry[] | undefined,
  incoming: ToolCallEntry[] | undefined,
): ToolCallEntry[] | undefined {
  if (incoming === undefined) return existing;
  if (existing === undefined) return incoming;
  const prevById = new Map(existing.map((t) => [t.id, t]));
  return incoming.map((t) => {
    const prev = prevById.get(t.id);
    return prev?.policyKind !== undefined && t.policyKind === undefined
      ? { ...t, policyKind: prev.policyKind }
      : t;
  });
}

export function reduceChatState(state: ChatState, action: ChatAction): ReducerResult {
  switch (action.type) {
    case 'chat/agent-history-merged': {
      // Reattach poll — merge incremental history into the timeline. New turns
      // are appended; an already-present turn is UPDATED IN PLACE so an
      // in-progress detached build fills in across polls instead of FREEZING at
      // its first-seen (partial) snapshot — the bug that made the GUI go silent
      // while a server-authoritative build streamed fine (each ~5s poll
      // re-projects the full journal, so the latest projection is the freshest
      // view of an observed turn). The ONE exception is the message currently
      // streaming locally (`currentAiMsgId`): the live stream mutates it at finer
      // granularity than this poll, so a stale poll must never clobber it — it
      // converges once the stream ends and `currentAiMsgId` clears. Inlined (not
      // `mergeHydrationMessages`) to avoid a state.ts ↔ project-agent-history.ts
      // runtime import cycle.
      //
      // #2893 Codex P1 — turn-aware supersession BEFORE the id merge: the
      // cloud dispatch does not forward the renderer's message ids to the
      // pod, so the journal mints its OWN ids and an id-only merge would
      // keep BOTH copies of a turn (stale local placeholder + journal twin)
      // as duplicate bubbles — in exactly the silent-completion path this
      // merge exists to backfill. Drop the superseded local rows (matched by
      // the verbatim user text; the live-streaming assistant and error
      // bubbles are never superseded — see turn-dedupe.ts) and keep the
      // journal copies, whose durable ids make every later merge converge
      // via plain id dedupe.
      if (action.messages.length === 0) return { state, effects: [] };
      const superseded = findSupersededTimelineRows(
        state.messages,
        action.messages,
        state.currentAiMsgId,
      );
      const byId = new Map<string, ChatMessage>();
      for (const m of state.messages) {
        if (!superseded.has(m.id)) byId.set(m.id, m);
      }
      let changed = superseded.size > 0;
      for (const m of action.messages) {
        const existing = byId.get(m.id);
        if (existing === undefined) {
          byId.set(m.id, m);
          changed = true;
          continue;
        }
        // Never clobber the locally-streaming message mid-stream.
        //
        // #2949 — "locally-streaming" means a RENDERER-minted placeholder
        // (`startTurn` / the autofire path, `source: 'renderer'`), whose live
        // chunk stream mutates it at finer granularity than this poll. An
        // `agent-stream` row — minted by the journal projection, or persisted
        // by desktop main's chat-stream-persistence and rehydrated (Codex LOW
        // on #2960: the source value is main/journal-owned, not projection-
        // exclusive) — adopted as `currentAiMsgId` by `rehydrateInFlightTurn`
        // on a mid-build reload has NO live stream in this renderer — the
        // projection IS its only update source, so the unconditional skip
        // froze the observed in-flight bubble at its first-seen snapshot for
        // the build's entire life. Skip only when the existing row is not an
        // `agent-stream` row (conservative for legacy source-less rows).
        if (m.id === state.currentAiMsgId && existing.source !== 'agent-stream') continue;
        // Update an observed message only if it actually advanced (avoids
        // re-render churn when a poll returns an unchanged turn). FIELD-MERGE
        // rather than replace: the projection only sets the streamed fields
        // (content/blocks/toolCalls/streamStatus), so a wholesale swap would
        // silently drop renderer-only fields the existing copy carries that the
        // journal projection never reconstructs — `error` (the structured
        // <CrucibleErrorState> payload) and `checkpoint` (top-level), and tool
        // `policyKind` (NESTED in toolCalls[] — a shallow spread would swap the
        // whole array and strip it, so merge those per-id via
        // mergeReattachToolCalls). Spreading `existing` first preserves the
        // top-level fields; `m` wins on the fields it does set.
        if (!sameRenderedAgentMessage(existing, m)) {
          byId.set(m.id, {
            ...existing,
            ...m,
            toolCalls: mergeReattachToolCalls(existing.toolCalls, m.toolCalls),
          });
          changed = true;
        }
      }
      const projectedIds = new Set(action.messages.map((m) => m.id));
      if (action.authoritative === true) {
        // #3051 — only a cold/full projection is authoritative about ABSENCE:
        // the warm accumulator may still legitimately carry rows a shrunk
        // journal dropped. These guards delete only absent journal rows,
        // never renderer placeholders, the current stream, errors, or checkpoints.
        //
        // #3528 (Bugbot High) — the projection is authoritative only up to the
        // NEWEST row it contains. A cold read can race a just-landed turn: the
        // page it returns pre-dates that turn, so the row is "absent" purely
        // because the snapshot is older than it, not because it was deleted.
        // Once `agent-flight-reconciled` clears `currentAiMsgId` (see below),
        // that row loses its stream guard too. Bounding deletion to rows strictly
        // before the projection's high-water ORDER KEY (LEG 3, below) keeps a
        // newer-than-snapshot row (and closes the empty-projection nuke: an empty
        // cold page has no high-water, so it deletes nothing). A genuinely
        // rolled-back newest row heals on the next cold reconcile once the
        // high-water advances past it.
        //
        // #3528 (Codex r3, MAJOR) — the deletion PROVENANCE gate is
        // `journalProjected`, NOT `source === 'agent-stream'`. That source value
        // is minted by desktop main's chat-stream-persistence too (persisted to
        // chat-history.json, rehydrated via `chat/loaded`), and `agent.history@1`
        // is best-effort — so a completed turn on disk can be ABSENT from the
        // journal. Gating on `source` would delete such a disk-persisted turn
        // (data loss). `journalProjected` is set ONLY by
        // `projectAgentHistoryToChatMessages`, so a row is deletable-on-absence
        // only when a prior journal projection actually produced it; a
        // desktop-persisted/rehydrated row never carries the marker and is safe.
        //
        // #3528 (LEG 3, MAJOR) — order the deletion boundary on the FULL journal
        // total order (t, device_id, seq, id), NOT `timestamp` (ms) alone. The
        // prior `m.timestamp <= projectionWatermarkTs` used only the millisecond
        // and `<=`: a journal-owned row sharing the newest projected row's exact
        // `t` but from a DIFFERENT device (or a higher `seq`) that this snapshot
        // simply missed satisfied `ts <= watermark` and was wrongly DELETED,
        // even though the substrate order places it AFTER the watermark (i.e.
        // NOT covered by the snapshot). Each journal-projected row now carries
        // its minting line's order key (co-stamped with `journalProjected`); the
        // projection's high-water is the MAX order key it contains, and an absent
        // row is deleted ONLY when its key sorts STRICTLY before that high-water
        // by the same total order. Consequences preserved:
        //   - equal-ms second-device / higher-seq row → sorts AFTER → PRESERVED
        //     (the newer-than-snapshot race, formerly the ts `<=` false-delete);
        //   - a genuinely-superseded lower-order-key row absent from a fresh
        //     complete snapshot → sorts strictly within the window → DELETED;
        //   - an empty projection has NO high-water → deletes nothing (the
        //     empty-cold nuke guard, formerly watermark = -Infinity);
        //   - a row without an order key is never eligible (fail-closed) — in a
        //     live session every `journalProjected` row co-carries one.
        let highWater: JournalOrderKey | undefined;
        for (const projectedMsg of action.messages) {
          const key = projectedMsg.journalOrderKey;
          if (key === undefined) continue;
          if (highWater === undefined || compareJournalOrderKeys(key, highWater) > 0) {
            highWater = key;
          }
        }
        if (highWater !== undefined) {
          for (const m of [...byId.values()]) {
            if (
              m.journalProjected === true &&
              m.journalOrderKey !== undefined &&
              !projectedIds.has(m.id) &&
              compareJournalOrderKeys(m.journalOrderKey, highWater) < 0 &&
              m.id !== state.currentAiMsgId &&
              m.error === undefined &&
              m.streamStatus !== 'error' &&
              m.checkpoint === undefined &&
              m.role !== 'inline-checkpoint'
            ) {
              byId.delete(m.id);
              changed = true;
            }
          }
        }
      }
      if (!changed) return { state, effects: [] };
      const merged = [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
      return { state: { ...state, messages: merged }, effects: [] };
    }
    case 'chat/agent-flight-reconciled': {
      // Only the build-finished case acts: a non-originating renderer that
      // missed the terminal chunk would otherwise show "working" forever
      // (status FSM: idle ⇔ !isAiTyping). Clear the busy flags. Guard so we
      // never disturb a renderer that legitimately has nothing in flight or
      // is mid-stream when the build is reported still running.
      if (action.inFlight) return { state, effects: [] };
      if (!state.isAiTyping && state.currentAiMsgId === null) return { state, effects: [] };
      // #2799 Codex P1: reconciliation IS a turn boundary — main says
      // nothing is in flight, so treat it like a terminal chunk and
      // drain the queue. Without this, messages queued mid-turn (now
      // possibly carrying attachments) sat stranded and fired after
      // the user's NEXT send, out of order.
      return drainAfterTerminal({ ...state, isAiTyping: false, currentAiMsgId: null });
    }
    case 'chat/loaded': {
      // GAP-3 (gui-parity-plan.md Phase 2) — a successful load (even an
      // empty one) is the `'ready'` transition. Stamp it onto the base
      // state up front so every `chat/loaded` return branch below (auto-
      // fire, in-flight rehydrate, plain replace) inherits it via its
      // `...state` spread, and clear any prior hydration error.
      state = { ...state, hydrationStatus: 'ready', hydrationError: null };
      // Crucible #602 PR #C1 round-2 LOW 1b + round-3 LOW: strip
      // activity markers on every rehydrated assistant message
      // before they reach `messages` AND preserve any
      // `pendingNext` partial-marker fragment into
      // `pendingMarkers` keyed by message id. The runtime always
      // strips markers before append, so persisted history should
      // already be clean — but a future bug, downgrade, or out-
      // of-band write could leave a fragment in the saved content.
      //
      // Round-3 LOW: the previous code dropped `pendingNext` on
      // the floor. That silently lost any trailing partial-marker
      // bytes — they vanished from `message.content` AND weren't
      // carried into `pendingMarkers`. Preserving them means a
      // subsequent live text chunk that COMPLETES the marker
      // will still resolve correctly through the strip + parse
      // path.
      const rehydratedPending = new Map<string, PendingActivityMarker>();
      const messages = action.messages.map((m) => {
        if (m.role !== 'assistant') return m;
        const { stripped, pendingNext } = stripActivityMarkers('', m.content);
        if (pendingNext) {
          rehydratedPending.set(m.id, { text: pendingNext });
        }
        return { ...m, content: stripped };
      });
      if (action.agentInFlight === true) {
        return {
          state: rehydrateInFlightTurn({
            ...state,
            messages,
            pendingMarkers: rehydratedPending,
          }),
          effects: [],
        };
      }

      // Loading history is intentionally side-effect free. The orchestration
      // layer separately dispatches `chat/autofire-initial-prompt` only after
      // it has read the durable interview state and proved this is a fresh,
      // unstarted create. A lone initial-prompt row is not sufficient proof.
      return {
        state: { ...state, messages, pendingMarkers: rehydratedPending },
        effects: [],
      };
    }

    case 'chat/autofire-initial-prompt': {
      if (state.hydrationStatus !== 'ready' || state.isAiTyping) {
        return { state, effects: [] };
      }
      const initial = state.messages[0];
      if (
        state.messages.length !== 1 ||
        initial?.role !== 'user' ||
        !initial.id.startsWith(INITIAL_PROMPT_MESSAGE_ID_PREFIX)
      ) {
        return { state, effects: [] };
      }
      const now = action.now;
      const aiMsgId = `ai-${now}`;
      const turnId = `turn-${initial.id}-${aiMsgId}`;
      const placeholder: ChatMessage = {
        id: aiMsgId,
        role: 'assistant',
        content: '',
        timestamp: now,
        source: 'renderer',
        streamSeq: 0,
        streamStatus: 'in-flight',
        turnId,
      };
      return {
        state: {
          ...state,
          messages: [...state.messages, placeholder],
          isAiTyping: true,
          currentAiMsgId: aiMsgId,
          // Match `startTurn`'s convention: clear agents at the
          // start of a new turn so a previous-session leak can't
          // bleed into this autofire's agents map.
          agents: new Map(),
          // #202 — same intent for the synthetic main-agent row.
          mainAgent: null,
          // Crucible #602 PR #C1 — fresh load means no in-flight
          // assistant messages, so no held-back marker fragments
          // should carry over.
          pendingMarkers: new Map(),
        },
        effects: [
          {
            type: 'ipc/agent-send',
            gameId: state.gameId,
            message: initial.content,
            assistantMsgId: aiMsgId,
            userMessageId: initial.id,
            assistantMessageId: aiMsgId,
            userTimestamp: initial.timestamp,
            assistantTimestamp: now,
            turnId,
            initialAutofire: true,
          },
        ],
      };
    }

    case 'chat/hydration-retrying':
      // GAP-3 — user-driven retry of the SAME game. Flip back to
      // `'loading'` + clear the error so the surface re-shows the loading
      // affordance; leave `messages` / `chatInput` / queue alone (a same-
      // game retry must not discard a draft).
      return {
        state: { ...state, hydrationStatus: 'loading', hydrationError: null },
        effects: [],
      };

    case 'chat/hydration-failed':
      // GAP-3 (gui-parity-plan.md Phase 2) — terminal hydration failure.
      // The mount effect dispatches this once it has exhausted its bounded
      // retry budget for a retryable transport error, or immediately for a
      // non-retryable error (corrupt-history). `messages` is left untouched
      // (typically the empty array the reducer started with) so the surface
      // renders an error state, not a silent blank. No effects: recovery is
      // user-driven (the "Try again" button re-runs the mount load).
      return {
        state: { ...state, hydrationStatus: 'error', hydrationError: action.error },
        effects: [],
      };

    case 'chat/local-event': {
      if (state.messages.some((m) => m.id === action.id)) {
        return { state, effects: [] };
      }
      const row: ChatMessage = {
        id: action.id,
        role: 'assistant',
        content: action.content,
        timestamp: action.timestamp,
        source: 'renderer',
      };
      const idx = state.messages.findIndex((m) => m.timestamp > action.timestamp);
      const messages =
        idx < 0
          ? [...state.messages, row]
          : [...state.messages.slice(0, idx), row, ...state.messages.slice(idx)];
      return {
        state: { ...state, messages },
        effects: [],
      };
    }

    case 'user/type':
      return { state: { ...state, chatInput: action.text }, effects: [] };

    case 'user/add-attachment': {
      const cap = checkAttachmentCaps(state.pendingAttachments, action.attachment);
      if (cap.kind === 'reject') {
        return {
          state,
          effects: [{ type: 'ui/error', message: cap.message }],
        };
      }
      return {
        state: {
          ...state,
          pendingAttachments: [...state.pendingAttachments, action.attachment],
          // Index-aligned with pendingAttachments — see the field doc.
          pendingSelectionContexts: [
            ...state.pendingSelectionContexts,
            action.selectionContext ?? null,
          ],
        },
        effects: [],
      };
    }

    case 'user/remove-attachment':
      return {
        state: {
          ...state,
          pendingAttachments: state.pendingAttachments.filter((_, i) => i !== action.index),
          // Same-index removal keeps the parallel arrays aligned.
          pendingSelectionContexts: state.pendingSelectionContexts.filter(
            (_, i) => i !== action.index,
          ),
        },
        effects: [],
      };

    case 'user/send': {
      const trimmed = state.chatInput.trim();
      const attachments = state.pendingAttachments;
      if (!trimmed && attachments.length === 0) {
        return { state, effects: [] };
      }
      // Empty text + image: synth a placeholder so the model has at
      // least one text block (Anthropic API requires a non-empty text
      // block when one is supplied; see #193 for the multi-image
      // composer redesign that lets the user caption images).
      const baseText = trimmed || '(image attached)';
      // Select-to-chat P1 — prepend each attachment's formatted
      // `crucible-selection` block so the agent reads the selection
      // geometry inline next to the user's instruction. The blocks
      // are baked into `messageText` BEFORE the mid-turn fork below,
      // so a queued entry carries them the same way a direct send
      // does (#2799 made attachment sends queueable mid-turn).
      const selectionBlocks = state.pendingSelectionContexts.filter((c): c is string => c !== null);
      const messageText =
        selectionBlocks.length > 0 ? `${selectionBlocks.join('\n\n')}\n\n${baseText}` : baseText;
      if (state.isAiTyping) {
        // Mid-turn behaviour (#2799): queue the send — attachments
        // included — up to MAX_QUEUED_MESSAGES. Pre-#2799 an
        // attachment-bearing send was refused outright (the #193
        // Codex catch was about SILENTLY dropping the image on a
        // keyboard send; queueing it intact satisfies the same
        // concern without blocking the user). A full queue refuses
        // loudly and leaves the composer untouched — input and
        // attachments stay put so nothing is lost.
        if (state.queuedMessages.length >= MAX_QUEUED_MESSAGES) {
          return {
            state,
            effects: [
              {
                type: 'ui/error',
                message: `Message queue is full (${MAX_QUEUED_MESSAGES} waiting). Remove a queued message or wait for the agent to finish.`,
              },
            ],
          };
        }
        return {
          state: {
            ...state,
            queuedMessages: [...state.queuedMessages, { text: messageText, attachments }],
            chatInput: '',
            // Consumed into the queue entry — same lifecycle as a
            // direct send, just deferred to the turn boundary.
            pendingAttachments: [],
            pendingSelectionContexts: [],
          },
          effects: [],
        };
      }
      // No turn in flight — fire directly. Append the user message
      // immediately AND a pending assistant placeholder so streaming
      // text has somewhere to land. The placeholder's id is the
      // currentAiMsgId; it stays set until a terminal chunk
      // (`done` / `interrupted` / terminal `error`) arrives.
      return startTurn(state, messageText, attachments);
    }

    case 'user/send-text': {
      const messageText = action.text.trim();
      if (!messageText) {
        return { state, effects: [] };
      }
      if (state.isAiTyping) {
        // Programmatic sends respect the same cap as composer sends
        // (#2799) — a dispatcher looping on a stuck turn shouldn't
        // be able to grow the queue without bound.
        if (state.queuedMessages.length >= MAX_QUEUED_MESSAGES) {
          return {
            state,
            effects: [
              {
                type: 'ui/error',
                message: `Message queue is full (${MAX_QUEUED_MESSAGES} waiting). Remove a queued message or wait for the agent to finish.`,
              },
            ],
          };
        }
        return {
          state: {
            ...state,
            queuedMessages: [...state.queuedMessages, { text: messageText, attachments: [] }],
          },
          effects: [],
        };
      }
      return startTurn(state, messageText);
    }

    case 'user/unqueue':
      return {
        state: {
          ...state,
          queuedMessages: state.queuedMessages.filter((_, i) => i !== action.index),
        },
        effects: [],
      };

    case 'user/retry': {
      // Issue #369 — chat-bubble retry. Locate the errored message
      // and the user message immediately preceding it. The user's
      // text is the input that the bouncer / SDK rejected; that's
      // what we re-send.
      //
      // Defensive guards (in order):
      //   1. Refuse mid-turn — if `isAiTyping` is true, a turn is
      //      already running and a retry would race the bouncer
      //      again. The renderer-side debounce is the primary
      //      defence; this is belt-and-braces.
      //   2. Refuse if the errored message isn't found, or has no
      //      `error` set — stale dispatch (e.g. the message scrolled
      //      away mid-click) shouldn't pop a new turn.
      //   3. Refuse if the preceding message isn't a user-role one —
      //      the chat-bubble error mounts always sit underneath an
      //      assistant placeholder that came AFTER a user send; if
      //      the shape ever drifts, fall through quietly rather than
      //      replay the wrong text.
      if (state.isAiTyping) {
        return { state, effects: [] };
      }
      const erroredIndex = state.messages.findIndex((m) => m.id === action.messageId);
      if (erroredIndex < 0) return { state, effects: [] };
      const errored = state.messages[erroredIndex];
      if (!errored?.error) return { state, effects: [] };
      const prior = state.messages[erroredIndex - 1];
      if (!prior || prior.role !== 'user') return { state, effects: [] };
      const retryText = prior.content;
      if (!retryText) return { state, effects: [] };
      // Drop the errored assistant bubble from the timeline before
      // starting the new turn. The user message stays — `startTurn`
      // will append a FRESH user bubble + assistant placeholder for
      // the retry. Why not reuse the preceding user message? Because
      // `startTurn` is the one place that issues the IPC effect, sets
      // `isAiTyping=true`, mints a `currentAiMsgId`, and resets
      // per-turn slices (`agents`, `mainAgent`, `lastTurnSpend`,
      // `pendingAttachments`). Replicating that inline would drift
      // away from the canonical send path on every reducer change.
      const messagesWithoutError = state.messages.filter((_, i) => i !== erroredIndex);
      return startTurn({ ...state, messages: messagesWithoutError }, retryText);
    }

    case 'user/stop':
      // Stop clears the queue AND fires interrupt. A half-stop
      // (interrupt the current turn but keep firing the queue
      // afterward) would surprise the user who clicked Stop on the
      // whole flow. The eventual `chunk/interrupted` arrives and
      // sees an empty queue, no-ops.
      return {
        state: { ...state, queuedMessages: [] },
        effects: [{ type: 'ipc/agent-interrupt', gameId: state.gameId }],
      };

    case 'user/answer-question':
      // CRITICAL: option-button answers fire the gate IPC ONLY.
      // Pre-PR-#170 also fired a chat-message fallback that raced
      // the still-active turn into the bouncer ("Another message
      // is already in flight"). Tests pin the absence of any
      // `ipc/agent-send` effect on this action.
      return {
        state,
        effects: [
          {
            type: 'ipc/agent-answer',
            gameId: state.gameId,
            toolUseID: action.toolUseID,
            answers: { [action.questionText]: action.answer },
          },
        ],
      };

    case 'chunk/received':
      return reduceChunk(state, action.chunk);

    case 'ipc/send-failed': {
      // The IPC promise itself rejected (main-side throw before
      // emitting any chunks). Mark the placeholder assistant
      // message with the structured error and clear the typing
      // flag so the user can send again.
      //
      // Crucible #602 PR #C1 round-2 MED 1: this path clears
      // `currentAiMsgId` without going through the terminal-chunk
      // handlers, so any partial marker fragment held in
      // `pendingMarkers` for the failed message would otherwise
      // become orphaned (never flushed, never cleared). Mirror the
      // `done` / `interrupted` / `error` pattern: flush the
      // fragment AS-IS to the message content BEFORE we stamp the
      // structured error onto it, then drop the entry from
      // `pendingMarkers`. By the time the IPC throws there's no
      // way any further chunks can complete the marker, so
      // flushing as literal text matches the design's "on
      // finalise, pending is flushed AS-IS" rule (§3a).
      const flushed = flushPendingMarker({
        ...state,
        currentAiMsgId: action.assistantMsgId,
      });
      // `flushPendingMarker` keys off `currentAiMsgId`. We
      // temporarily pinned it to `action.assistantMsgId` so the
      // flush hits the right message even if a downstream concurrent
      // turn has already moved `currentAiMsgId` forward.
      // Only the failed send's own turn ends here. When a newer
      // concurrent turn already owns `currentAiMsgId`, that turn is
      // still streaming — preserve its typing state (flipping it
      // false would re-open the composer mid-stream and let the next
      // send race into the bouncer instead of queueing). When no
      // other turn is live, the flags settle to idle as before.
      const activeTurnFailed = state.currentAiMsgId === action.assistantMsgId;
      const failed: ChatState = {
        ...flushed,
        messages: flushed.messages.map((m) =>
          m.id === action.assistantMsgId ? { ...m, error: action.error } : m,
        ),
        isAiTyping: activeTurnFailed || state.currentAiMsgId === null ? false : state.isAiTyping,
        currentAiMsgId: activeTurnFailed ? null : state.currentAiMsgId,
      };
      // #2799 Codex P1: a rejected send is a turn boundary too — use
      // the same queue-finalisation policy as terminal chunks so a
      // message queued behind the failed turn fires now instead of
      // stranding and later delivering after the user's next send,
      // out of order. Guard: if a newer concurrent turn has already
      // moved `currentAiMsgId` forward, that turn is live — draining
      // would double-fire into the bouncer, so leave the queue for
      // ITS terminal chunk to drain.
      if (failed.currentAiMsgId !== null) {
        return { state: failed, effects: [] };
      }
      return drainAfterTerminal(failed);
    }

    case 'ipc/autofire-not-needed': {
      const activeTurnDeclined = state.currentAiMsgId === action.assistantMsgId;
      const placeholder = state.messages.find((message) => message.id === action.assistantMsgId);
      const isEmptyRendererPlaceholder =
        placeholder?.role === 'assistant' &&
        placeholder.source === 'renderer' &&
        placeholder.content.length === 0 &&
        placeholder.error === undefined;
      const pendingMarkers = new Map(state.pendingMarkers);
      pendingMarkers.delete(action.assistantMsgId);
      return {
        state: {
          ...state,
          messages: isEmptyRendererPlaceholder
            ? state.messages.filter((message) => message.id !== action.assistantMsgId)
            : state.messages,
          isAiTyping: activeTurnDeclined ? false : state.isAiTyping,
          currentAiMsgId: activeTurnDeclined ? null : state.currentAiMsgId,
          pendingMarkers,
        },
        effects: [],
      };
    }

    case 'deps/request': {
      // Crucible #548 — append to the queue. Defensively drop a
      // duplicate sessionId so a re-pushed request (e.g. a renderer
      // re-subscribe after a brief disconnect) doesn't double-stack.
      // The first occurrence wins; the duplicate is silently ignored.
      const alreadyQueued = state.pendingDependencyRequests.some(
        (r) => r.sessionId === action.request.sessionId,
      );
      if (alreadyQueued) {
        return { state, effects: [] };
      }
      return {
        state: {
          ...state,
          pendingDependencyRequests: [...state.pendingDependencyRequests, action.request],
        },
        effects: [],
      };
    }

    case 'deps/approve': {
      // Crucible #548 — user clicked Install. Pop the matching entry
      // (by sessionId — order-preserving) and emit the IPC effect.
      // Main shells out to `pnpm add` and the tool result lands on
      // the agent's next message; the modal is already gone.
      // #3059 — carry the matched request on the effect so the effect
      // handler can re-enqueue it (restoring the modal) if the IPC rejects,
      // rather than silently stranding the gate. A stray dispatch for a
      // session no longer queued (e.g. a double-click after the first pop)
      // matches nothing and emits NOTHING — no phantom deps:respond.
      const matched = state.pendingDependencyRequests.find((r) => r.sessionId === action.sessionId);
      if (matched === undefined) return { state, effects: [] };
      return {
        state: {
          ...state,
          pendingDependencyRequests: state.pendingDependencyRequests.filter(
            (r) => r.sessionId !== action.sessionId,
          ),
        },
        effects: [
          {
            type: 'ipc/deps-respond',
            sessionId: action.sessionId,
            approved: true,
            finalVersion: action.finalVersion,
            request: matched,
          },
        ],
      };
    }

    case 'deps/decline': {
      // Crucible #548 — user declined / dismissed. Same shape as
      // approve, different effect payload. The agent reads back the
      // reason text as the tool result; mirror keeps the renderer's
      // surface symmetrical with main-side gate's two-branch result.
      // #3059 — same request-carry + stray-dispatch guard as approve.
      const matched = state.pendingDependencyRequests.find((r) => r.sessionId === action.sessionId);
      if (matched === undefined) return { state, effects: [] };
      return {
        state: {
          ...state,
          pendingDependencyRequests: state.pendingDependencyRequests.filter(
            (r) => r.sessionId !== action.sessionId,
          ),
        },
        effects: [
          {
            type: 'ipc/deps-respond',
            sessionId: action.sessionId,
            approved: false,
            reason: action.reason,
            request: matched,
          },
        ],
      };
    }

    case 'template-file-override/request': {
      // Crucible #3491 — append to the queue. Same defensive
      // duplicate-sessionId drop as `deps/request`.
      const alreadyQueued = state.pendingTemplateFileOverrideRequests.some(
        (r) => r.sessionId === action.request.sessionId,
      );
      if (alreadyQueued) {
        return { state, effects: [] };
      }
      return {
        state: {
          ...state,
          pendingTemplateFileOverrideRequests: [
            ...state.pendingTemplateFileOverrideRequests,
            action.request,
          ],
        },
        effects: [],
      };
    }

    case 'template-file-override/approve': {
      // Crucible #3491 — user clicked Delete file. Pop the matching
      // entry and emit the IPC effect; the interpreter re-enqueues on
      // a failed IPC call (mirrors `deps/approve` / #3059).
      const matched = state.pendingTemplateFileOverrideRequests.find(
        (r) => r.sessionId === action.sessionId,
      );
      if (matched === undefined) return { state, effects: [] };
      return {
        state: {
          ...state,
          pendingTemplateFileOverrideRequests: state.pendingTemplateFileOverrideRequests.filter(
            (r) => r.sessionId !== action.sessionId,
          ),
        },
        effects: [
          {
            type: 'ipc/template-file-override-respond',
            sessionId: action.sessionId,
            approved: true,
            request: matched,
          },
        ],
      };
    }

    case 'template-file-override/decline': {
      // Crucible #3491 — user declined / dismissed. Same shape as
      // approve, different effect payload.
      const matched = state.pendingTemplateFileOverrideRequests.find(
        (r) => r.sessionId === action.sessionId,
      );
      if (matched === undefined) return { state, effects: [] };
      return {
        state: {
          ...state,
          pendingTemplateFileOverrideRequests: state.pendingTemplateFileOverrideRequests.filter(
            (r) => r.sessionId !== action.sessionId,
          ),
        },
        effects: [
          {
            type: 'ipc/template-file-override-respond',
            sessionId: action.sessionId,
            approved: false,
            reason: action.reason,
            request: matched,
          },
        ],
      };
    }

    case 'chat/checkpoint-received': {
      // Crucible #602 PR #C4 — inline-checkpoint reducer action
      // (design §9 "Reducer action"). Pure data transform: no
      // effects, no IPC. The dispatcher (Issue E — Workspace
      // subscription) is what gates on the
      // `CRUCIBLE_AGENT_TRANSPARENCY` flag; by the time the action
      // reaches this branch we're committed to the synthesis.
      //
      // Four guards, in order:
      //   1. Cross-game — drop if `action.gameId !== state.gameId`.
      //      Mirrors the chunk handlers' cross-game safety; cheap
      //      defence against a stale subscription's push landing
      //      after the Workspace navigated to a different game.
      //   2. Duplicate by id — if a row with the checkpoint's id
      //      already exists in `messages`, no-op. Idempotent under
      //      re-render / replay (the emitter could in theory fire
      //      twice during a race + the disk read on Workspace
      //      remount could surface the same row through
      //      `chat:load` before the IPC push lands).
      //   3. Insertion by `timestamp` — single linear scan to find
      //      the right slot. Typically lands at the end (checkpoints
      //      arrive newest-last), so the loop terminates fast; the
      //      walk also handles out-of-order pushes cleanly
      //      (e.g. the Workspace remount race in §2.1 where
      //      `chat:load` rehydrates older rows AFTER a live push has
      //      already injected newer ones).
      //   4. Denormalise `content` — one-line plaintext rendering
      //      per design §6.2. Generated deterministically from the
      //      structured `checkpoint` payload so `chat:save` round-
      //      trips it without drift.
      if (action.gameId !== state.gameId) {
        return { state, effects: [] };
      }
      if (state.messages.some((m) => m.id === action.checkpoint.id)) {
        return { state, effects: [] };
      }
      const checkpoint = action.checkpoint;
      const newRow = checkpointToChatMessage(checkpoint);
      const streamingState = insertCheckpointIntoStreamingTurn(state, newRow);
      if (streamingState) {
        return { state: streamingState, effects: [] };
      }
      return {
        state: { ...state, messages: insertMessageByTimestamp(state.messages, newRow) },
        effects: [],
      };
    }

    case 'game/changed':
      // Workspace navigated to another game (React Router reuses
      // the component instance for /workspace/:gameId, so we don't
      // unmount). Full reset to `initialChatState(newGameId)`:
      // messages, queue, in-flight, AND chatInput are all wiped.
      // Cursor catch on PR #166 motivated this branch — without it,
      // a queue full of messages for game A would fire against
      // game B when B's first turn finished. Wiping `chatInput`
      // too is the consistent choice: anything in flight against
      // game A is meaningless against game B.
      return {
        state: initialChatState(action.gameId),
        effects: [],
      };
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Append the user message + a pending assistant placeholder, set
 * `isAiTyping=true`, return the `ipc/agent-send` effect. Used by both
 * `user/send` and the queue-drain branch of the chunk handler.
 *
 * `attachments` flows through both paths since #2799: `user/send`
 * passes the pending chips directly, and queue-drain passes the
 * queued entry's own attachments (queued messages carry their images
 * with them — see `QueuedChatMessage`).
 */
function startTurn(
  state: ChatState,
  text: string,
  attachments: readonly AgentSendAttachment[] = [],
): ReducerResult {
  const now = Date.now();
  const userMsgId = `user-${now}`;
  const aiMsgId = `ai-${now + 1}`;
  const turnId = `turn-${userMsgId}-${aiMsgId}`;
  const userMsg: ChatMessage = {
    id: userMsgId,
    role: 'user',
    content: text,
    timestamp: now,
    source: 'renderer',
    turnId,
  };
  const aiPlaceholder: ChatMessage = {
    id: aiMsgId,
    role: 'assistant',
    content: '',
    timestamp: now + 1,
    source: 'renderer',
    streamSeq: 0,
    streamStatus: 'in-flight',
    turnId,
  };
  return {
    state: {
      ...state,
      messages: [...state.messages, userMsg, aiPlaceholder],
      chatInput: '',
      isAiTyping: true,
      currentAiMsgId: aiMsgId,
      // #3021 — a fresh turn starts with no transient preview.
      livePreview: null,
      // Consumed by this turn — attachments don't survive a send.
      pendingAttachments: [],
      // Parallel array — consumed with its attachments (the blocks
      // are already baked into `text` by the `user/send` branch).
      pendingSelectionContexts: [],
      // Clear the agents map at the start of every turn so the
      // previous turn's specialists don't bleed into this one.
      // Within a single turn the map accumulates; the right-column
      // panel renders rows for everything (active + recently-stopped)
      // until the next turn starts.
      agents: new Map(),
      // #202 — same intent for the synthetic main-agent row.
      mainAgent: null,
      // Phase 3 — clear last-turn cost / turn count so the chat
      // header strip blanks while a new turn is in flight. The
      // SDK's result chunk repopulates them at turn-end.
      lastTurnSpend: null,
      // Crucible #602 PR #C1 — new turn starts with a fresh
      // assistant message id; no held-back marker fragments from
      // any earlier message can survive into the new bubble.
      pendingMarkers: new Map(),
    },
    effects: [
      {
        type: 'ipc/agent-send',
        gameId: state.gameId,
        message: text,
        assistantMsgId: aiMsgId,
        userMessageId: userMsgId,
        assistantMessageId: aiMsgId,
        userTimestamp: now,
        assistantTimestamp: now + 1,
        turnId,
        ...(attachments.length > 0 ? { attachments } : {}),
      },
    ],
  };
}

/**
 * Flush the pending marker fragment for the active assistant
 * message back into its `content` AS-IS. Called from terminal
 * chunk handlers (`done` / `interrupted` / `error`) — by then no
 * further chunks can complete the marker, so a hanging fragment is
 * treated as accidental literal text rather than silently
 * suppressed (design §3a test #5). Idempotent + no-op when there's
 * no pending fragment.
 *
 * Returns a new `ChatState` with the pending fragment moved into
 * the matching message's content and removed from
 * `pendingMarkers`. Callers that go on to update the same message
 * must read from the returned state, not the input.
 *
 * Crucible #602 PR #C1.
 */
function flushPendingMarker(state: ChatState): ChatState {
  const aiId = state.currentAiMsgId;
  if (!aiId) return state;
  const pending = state.pendingMarkers.get(aiId);
  if (!pending) return state;
  const nextPending = new Map(state.pendingMarkers);
  nextPending.delete(aiId);
  return {
    ...state,
    pendingMarkers: nextPending,
    messages: state.messages.map((m) =>
      m.id === aiId ? { ...m, content: (m.content ?? '') + pending.text } : m,
    ),
  };
}

/**
 * Render the denormalised `content` string for an inline-checkpoint
 * row (design §6.2). Delegates to the shared
 * `denormaliseCheckpointContent` (`src/shared/checkpoint-content.ts`)
 * so the live IPC push path through the reducer and the main-side
 * `inline-checkpoint-emitter` write to `chat-history.json` produce
 * byte-identical content for the same checkpoint payload
 * (Crucible #785 — Codex round-1 HIGH).
 *
 * Three reasons content is denormalised rather than left empty:
 *   1. `analyze-timing` + any future plaintext walker get a sensible
 *      fallback even without knowing the discriminator.
 *   2. The `<ActivityCheckpointMessage>` renderer can soft-fall-back
 *      to `content` if a future schema-drift introduces a trigger
 *      value an older Foundry can't render structurally.
 *   3. Every `ChatMessage` row keeps the same four required fields
 *      (`id` / `role` / `content` / `timestamp`); `checkpoint` is
 *      the new optional. Uniform shape ages better.
 *
 * Per-trigger format (mirrors `denormaliseCheckpointContent`):
 *
 *   phase-transition    → `<build-flow phase> begins — <subsystem> — <action>`
 *   specialist-dispatch → `Specialist dispatched — <specialist> · <subsystem>`
 *                         (pluralises to "Specialists dispatched" for 2+)
 *   subsystem-switch    → `Now: <subsystem> — <action>`
 *   time-cap            → `Still on <subsystem> — <action>`
 *
 * Null wire fields fall back to the shared `Starting up` /
 * `Reading project context` / `specialist` strings — never an empty
 * `content`, which would silently break the §10.5 marker-strip-safety
 * fixture and downstream plaintext walkers.
 *
 * Crucible #602 PR #C4; #785 unified the formatter across surfaces.
 */
export function renderCheckpointContent(checkpoint: InlineCheckpoint): string {
  return denormaliseCheckpointContent(checkpoint);
}

export function checkpointToChatMessage(checkpoint: InlineCheckpoint): ChatMessage {
  return {
    id: checkpoint.id,
    role: 'inline-checkpoint',
    content: renderCheckpointContent(checkpoint),
    timestamp: checkpoint.timestamp,
    checkpoint,
  };
}

function insertMessageByTimestamp(
  messages: readonly ChatMessage[],
  row: ChatMessage,
): ChatMessage[] {
  const idx = messages.findIndex((m) => m.timestamp > row.timestamp);
  return idx < 0 ? [...messages, row] : [...messages.slice(0, idx), row, ...messages.slice(idx)];
}

function latestInFlightAssistantMessage(messages: readonly ChatMessage[]): ChatMessage | null {
  let latest: ChatMessage | null = null;
  for (const message of messages) {
    if (message.role !== 'assistant' || message.streamStatus !== 'in-flight') continue;
    if (latest === null || message.timestamp >= latest.timestamp) {
      latest = message;
    }
  }
  return latest;
}

function rehydrateInFlightTurn(state: ChatState): ChatState {
  const active = latestInFlightAssistantMessage(state.messages);
  if (active === null) return state;

  return {
    ...state,
    isAiTyping: true,
    currentAiMsgId: active.id,
    queuedMessages: [],
    agents: new Map(),
    mainAgent: null,
    lastTurnSpend: null,
  };
}

function hasAssistantStreamPayload(message: ChatMessage): boolean {
  return Boolean(
    message.content.length > 0 || (message.toolCalls?.length ?? 0) > 0 || message.error,
  );
}

function transferPendingMarker(
  pendingMarkers: ReadonlyMap<string, PendingActivityMarker>,
  fromId: string,
  toId: string,
): ReadonlyMap<string, PendingActivityMarker> {
  const pending = pendingMarkers.get(fromId);
  if (!pending) return pendingMarkers;
  const next = new Map(pendingMarkers);
  next.delete(fromId);
  next.set(toId, pending);
  return next;
}

function insertAfterRow(
  messages: readonly ChatMessage[],
  anchorId: string,
  row: ChatMessage,
): ChatMessage[] {
  const anchorIndex = messages.findIndex((m) => m.id === anchorId);
  if (anchorIndex < 0) return [...messages, row];
  return [...messages.slice(0, anchorIndex + 1), row, ...messages.slice(anchorIndex + 1)];
}

function insertCheckpointIntoStreamingTurn(
  state: ChatState,
  checkpointRow: ChatMessage,
): ChatState | null {
  if (!state.isAiTyping || !state.currentAiMsgId) return null;
  const activeIndex = state.messages.findIndex(
    (m) => m.id === state.currentAiMsgId && m.role === 'assistant',
  );
  if (activeIndex < 0) return null;
  const active = state.messages[activeIndex];
  if (!active || checkpointRow.timestamp < active.timestamp) return null;

  if (!hasAssistantStreamPayload(active)) {
    const activeAfterCheckpoint: ChatMessage = {
      ...active,
      timestamp: checkpointRow.timestamp,
    };
    const withoutActive = [
      ...state.messages.slice(0, activeIndex),
      ...state.messages.slice(activeIndex + 1),
    ];
    const withCheckpoint = insertMessageByTimestamp(withoutActive, checkpointRow);
    return {
      ...state,
      messages: insertAfterRow(withCheckpoint, checkpointRow.id, activeAfterCheckpoint),
    };
  }

  const continuationId = `${active.id}-after-${checkpointRow.id}`;
  const continuation: ChatMessage = {
    id: continuationId,
    role: 'assistant',
    content: '',
    timestamp: checkpointRow.timestamp,
    source: active.source ?? 'renderer',
    streamSeq: 0,
    streamStatus: 'in-flight',
    ...(active.turnId !== undefined ? { turnId: active.turnId } : {}),
  };
  const beforeAndActive = state.messages.slice(0, activeIndex + 1);
  const afterActive = state.messages.slice(activeIndex + 1);
  const afterWithCheckpoint = insertMessageByTimestamp(afterActive, checkpointRow);
  return {
    ...state,
    currentAiMsgId: continuationId,
    pendingMarkers: transferPendingMarker(state.pendingMarkers, active.id, continuationId),
    messages: insertAfterRow(
      [...beforeAndActive, ...afterWithCheckpoint],
      checkpointRow.id,
      continuation,
    ),
  };
}

function updateToolCallById(
  messages: readonly ChatMessage[],
  toolCallId: string,
  turnId: string | undefined,
  update: (toolCall: ToolCallEntry) => ToolCallEntry,
): { messages: ChatMessage[]; found: boolean } {
  let found = false;
  const nextMessages = messages.map((m) => {
    if (turnId !== undefined && m.turnId !== turnId) return m;
    const toolCalls = m.toolCalls;
    if (!toolCalls?.some((t) => t.id === toolCallId)) return m;
    found = true;
    return {
      ...m,
      toolCalls: toolCalls.map((t) => (t.id === toolCallId ? update(t) : t)),
    };
  });
  return { messages: nextMessages, found };
}

/**
 * Append an ordered content block to an assistant message's `blocks`
 * list, in EMIT ORDER (see `MessageBlock` in `chat/types.ts`). Returns a
 * NEW messages array with the target message's `blocks` updated; every
 * other message is returned by reference (cheap, React-reconciler
 * friendly).
 *
 * Coalescing rule: a `text` or `thinking` block extends the message's
 * CURRENT TAIL block when that tail is the same kind AND same `agentId` — so a
 * turn that streams "Foo" then "bar" as two text chunks yields one `text` block
 * "Foobar", while "Foo" → tool_call → "bar" yields THREE blocks
 * (text / tool_call / text), preserving the interleave. A block emitted inside a
 * sub-agent (carrying `agentId`) never coalesces with a main-thread block of the
 * same kind — the attribution boundary forces a new block. `tool_call` blocks
 * never coalesce (each is a distinct card) and de-dup on `toolCallId` so a
 * re-delivered `tool_call` chunk can't double-add. `subagent` markers never
 * coalesce and de-dup on `(phase, agentId)` so a re-delivered lifecycle chunk
 * can't double-add.
 */
function appendBlock(
  messages: readonly ChatMessage[],
  targetId: string,
  block: MessageBlock,
): ChatMessage[] {
  return messages.map((m) => {
    if (m.id !== targetId) return m;
    const prev = m.blocks ?? [];
    const tail = prev[prev.length - 1];
    if (
      (block.kind === 'text' || block.kind === 'thinking') &&
      tail &&
      tail.kind === block.kind &&
      // Same attribution — a sub-agent's text must not merge into the main
      // thread's (or another sub-agent's) run.
      tail.agentId === block.agentId &&
      // Retraction must be able to remove one SDK frame without deleting a
      // neighbouring frame that happened to emit the same block kind.
      tail.sourceMessageUuid === block.sourceMessageUuid
    ) {
      // Extend the contiguous run rather than starting a new block.
      const merged: MessageBlock = {
        kind: block.kind,
        text: tail.text + block.text,
        ...(block.agentId !== undefined ? { agentId: block.agentId } : {}),
        ...(block.sourceMessageUuid !== undefined
          ? { sourceMessageUuid: block.sourceMessageUuid }
          : {}),
      };
      return { ...m, blocks: [...prev.slice(0, -1), merged] };
    }
    if (block.kind === 'tool_call') {
      // De-dup: an upsert-style re-delivery must not add a second card.
      if (prev.some((b) => b.kind === 'tool_call' && b.toolCallId === block.toolCallId)) {
        return m;
      }
    }
    if (block.kind === 'subagent') {
      // De-dup on (phase, agentId): a re-delivered start/stop must not double-add.
      if (
        prev.some(
          (b) => b.kind === 'subagent' && b.phase === block.phase && b.agentId === block.agentId,
        )
      ) {
        return m;
      }
    }
    return { ...m, blocks: [...prev, block] };
  });
}

/**
 * Per-image and per-message size caps for the screenshot-paste MVP
 * (#193). Identical to the main-side `validateAttachments` numbers —
 * the renderer pre-checks so we can show a chip-rejection toast
 * synchronously without burning an IPC round-trip; the adapter
 * re-checks defensively in case a future direct-IPC path bypasses
 * the renderer.
 */
export const MAX_ATTACHMENT_BYTES_PER_IMAGE = 5 * 1024 * 1024;
export const MAX_ATTACHMENT_BYTES_PER_MESSAGE = 10 * 1024 * 1024;

/**
 * Remove every renderer projection sourced from retracted SDK messages.
 *
 * Args:
 *   state: Current chat state.
 *   rawUuids: Untrusted UUID list from the stream boundary.
 *
 * Returns:
 *   State without matching transcript blocks, tools, or activity chunks.
 *
 * Raises:
 *   Never.
 */
function retractSourceMessages(state: ChatState, rawUuids: unknown): ChatState {
  if (!Array.isArray(rawUuids)) return state;
  const uuids = new Set(
    rawUuids.filter((uuid): uuid is string => typeof uuid === 'string' && uuid.trim().length > 0),
  );
  if (uuids.size === 0) return state;

  let changed = false;
  const messages = state.messages.map((message) => {
    const originalBlocks = message.blocks;
    const originalTools = message.toolCalls;
    const toolCalls = originalTools?.filter(
      (tool) =>
        !(
          (tool.sourceMessageUuid && uuids.has(tool.sourceMessageUuid)) ||
          (tool.toolResultSourceMessageUuid && uuids.has(tool.toolResultSourceMessageUuid))
        ),
    );
    const removedToolIds = new Set(
      (originalTools ?? [])
        .filter((tool) => !toolCalls?.some((survivor) => survivor.id === tool.id))
        .map((tool) => tool.id),
    );
    const blocks = originalBlocks?.filter(
      (block) =>
        !(
          block.kind !== 'subagent' &&
          block.sourceMessageUuid &&
          uuids.has(block.sourceMessageUuid)
        ) && !(block.kind === 'tool_call' && removedToolIds.has(block.toolCallId)),
    );
    if (blocks?.length === originalBlocks?.length && toolCalls?.length === originalTools?.length) {
      return message;
    }
    changed = true;
    // Legacy content remains a projection of surviving visible text blocks.
    const content = blocks
      ? blocks
          .filter(
            (block): block is Extract<MessageBlock, { kind: 'text' }> => block.kind === 'text',
          )
          .map((block) => block.text)
          .join('')
      : message.content;
    return { ...message, blocks, toolCalls, content };
  });

  const agents = new Map(state.agents);
  for (const [agentId, row] of agents) {
    const recentChunks = row.recentChunks.filter(
      (recent) => !recent.sourceMessageUuid || !uuids.has(recent.sourceMessageUuid),
    );
    if (recentChunks.length !== row.recentChunks.length) {
      changed = true;
      agents.set(agentId, { ...row, recentChunks });
    }
  }
  const mainChunks = state.mainAgent?.recentChunks.filter(
    (recent) => !recent.sourceMessageUuid || !uuids.has(recent.sourceMessageUuid),
  );
  const mainChanged =
    state.mainAgent !== null && mainChunks?.length !== state.mainAgent.recentChunks.length;
  if (mainChanged) changed = true;
  const pendingMarkers = new Map(state.pendingMarkers);
  let pendingChanged = false;
  if (state.currentAiMsgId) {
    const pending = pendingMarkers.get(state.currentAiMsgId);
    if (pending?.sourceMessageUuid && uuids.has(pending.sourceMessageUuid)) {
      pendingMarkers.delete(state.currentAiMsgId);
      pendingChanged = true;
    }
  }
  if (!changed && !pendingChanged) return state;
  return {
    ...state,
    messages,
    agents,
    pendingMarkers,
    mainAgent:
      state.mainAgent && mainChunks
        ? { ...state.mainAgent, recentChunks: mainChunks }
        : state.mainAgent,
  };
}

/**
 * Decide whether `next` can be appended to `existing` without
 * exceeding the per-image / per-message MVP caps. Returns either
 * `{kind:'ok'}` or `{kind:'reject', message}` so the reducer can
 * surface a single error effect with user-friendly copy.
 */
function checkAttachmentCaps(
  existing: readonly AgentSendAttachment[],
  next: AgentSendAttachment,
): { kind: 'ok' } | { kind: 'reject'; message: string } {
  if (next.byteLength > MAX_ATTACHMENT_BYTES_PER_IMAGE) {
    const mb = (next.byteLength / 1024 / 1024).toFixed(1);
    return {
      kind: 'reject',
      message: `That image is ${mb} MB; the per-image cap is 5 MB.`,
    };
  }
  const total = existing.reduce((acc, a) => acc + a.byteLength, 0) + next.byteLength;
  if (total > MAX_ATTACHMENT_BYTES_PER_MESSAGE) {
    const mb = (total / 1024 / 1024).toFixed(1);
    return {
      kind: 'reject',
      message: `Attaching this image would push the message to ${mb} MB; the per-message cap is 10 MB.`,
    };
  }
  return { kind: 'ok' };
}

/**
 * Apply an agent-stream chunk to state. Handles `text` (append),
 * `tool_call` / `tool_result` (upsert into the active assistant
 * message), `done` / `interrupted` (terminal — flip `isAiTyping=false`
 * and drain the queue), `error` (split: terminal flips, non-terminal
 * keeps the bubble active for subsequent chunks).
 *
 * Drain semantics: after a terminal chunk, if `queuedMessages` is
 * non-empty, pop the head and start a new turn. The reducer is pure,
 * so the new turn's `ipc/agent-send` effect rides out alongside any
 * other effects this chunk produced.
 */
function reduceChunk(state: ChatState, chunk: AgentStreamChunk): ReducerResult {
  const result = reduceChunkInner(state, chunk);
  // #202 — final pass: reconcile `mainAgent.status` against the
  // post-reduce `isAiTyping`. Terminal chunks (`done`, `interrupted`,
  // terminal `error`) flip `isAiTyping=false` but don't themselves
  // touch `mainAgent` in `applyAgentTracking`; this pass settles
  // the row to `'idle'` so the panel renders the correct pill.
  return { ...result, state: withMainAgentStatus(result.state) };
}

function reduceChunkInner(state: ChatState, chunk: AgentStreamChunk): ReducerResult {
  if (chunk.type === 'message_retraction') {
    return {
      state: retractSourceMessages(state, chunk.retractedMessageUuids),
      effects: [],
    };
  }
  // Phase 1 of the Agents-panel work: fold any subagent tracking
  // into the agents map BEFORE the per-type switch. This way every
  // existing case below operates on a state that already has the
  // updated agents map, and we don't have to merge it into each
  // branch's return.
  const tracked = applyAgentTracking(state, chunk);
  switch (chunk.type) {
    case 'agent_started':
    case 'agent_stopped': {
      // Tracked into the agents map above (right-column panel). ALSO thread an
      // inline `subagent` marker into the active assistant transcript in emit
      // order (Claude-Code parity) so the user sees "dispatched art-director"
      // and "art-director finished" exactly where they happened — not only in
      // the side panel. No active bubble (lifecycle chunk arrived between turns)
      // → panel-only, as before.
      const aiId = tracked.currentAiMsgId;
      const info = chunk.subagent;
      if (!aiId || !info) return { state: tracked, effects: [] };
      const block: MessageBlock =
        chunk.type === 'agent_started'
          ? { kind: 'subagent', phase: 'start', agentId: info.agentId, agentType: info.agentType }
          : {
              kind: 'subagent',
              phase: 'stop',
              agentId: info.agentId,
              agentType: info.agentType,
              ...(info.stopReason ? { stopReason: info.stopReason } : {}),
            };
      return {
        state: { ...tracked, messages: appendBlock(tracked.messages, aiId, block) },
        effects: [],
      };
    }

    case 'preview': {
      // #3021 — a transient in-progress preview of the model's live output.
      // Accumulated into `livePreview` ONLY: never appended to a message's
      // `content` / `blocks`, never journaled. The complete assistant message
      // REPLACES it (the text / thinking / tool_call cases clear it) and every
      // terminal-reset seam nulls it out. A run of preview deltas concatenates
      // in emit order. Empty delta is a no-op so we never allocate a "" preview.
      const delta = chunk.preview?.delta ?? '';
      if (delta.length === 0) return { state: tracked, effects: [] };
      return {
        state: { ...tracked, livePreview: (tracked.livePreview ?? '') + delta },
        effects: [],
      };
    }

    case 'text': {
      const aiId = tracked.currentAiMsgId;
      if (!aiId || !chunk.text) return { state: tracked, effects: [] };
      // Crucible #602 PR #C1: strip activity-annotation markers
      // BEFORE appending to `message.content`. The strip is
      // authoritative — main's parser is a side-channel reader; the
      // renderer's reducer is the single site that decides whether
      // marker text reaches the rendered chat. See `chat/strip-
      // activity-markers.ts` + design §3a.
      const prevPending = tracked.pendingMarkers.get(aiId);
      const crossesSdkMessages = Boolean(
        prevPending?.sourceMessageUuid &&
        chunk.sourceMessageUuid &&
        prevPending.sourceMessageUuid !== chunk.sourceMessageUuid,
      );
      // A marker cannot span two distinct SDK messages. If a fallback message
      // arrives before the refused message is retracted, flush the old fragment
      // as literal text and parse the new message independently.
      const { stripped, pendingNext } = stripActivityMarkers(
        crossesSdkMessages ? '' : (prevPending?.text ?? ''),
        chunk.text,
      );
      const nextPending = new Map(tracked.pendingMarkers);
      if (pendingNext) {
        const sourceMessageUuid = crossesSdkMessages
          ? chunk.sourceMessageUuid
          : (prevPending?.sourceMessageUuid ?? chunk.sourceMessageUuid);
        nextPending.set(aiId, {
          text: pendingNext,
          ...(sourceMessageUuid ? { sourceMessageUuid } : {}),
        });
      } else {
        nextPending.delete(aiId);
      }
      let withContent = tracked.messages;
      if (crossesSdkMessages && prevPending) {
        const flushedPrevious = redactSecrets(prevPending.text);
        withContent = withContent.map((m) =>
          m.id === aiId ? { ...m, content: (m.content ?? '') + flushedPrevious } : m,
        );
        if (flushedPrevious.length > 0) {
          withContent = appendBlock(withContent, aiId, {
            kind: 'text',
            text: flushedPrevious,
            ...(prevPending.sourceMessageUuid
              ? { sourceMessageUuid: prevPending.sourceMessageUuid }
              : {}),
          });
        }
      }
      const visibleText = redactSecrets(stripped);
      // Append to the legacy `content` blob (back-compat + persistence)
      // AND the ordered `blocks` list (interleaved render). A run of
      // text chunks with no intervening tool/thinking block coalesces
      // into one text block (see `appendBlock`). Skip a no-op empty
      // delta so we don't create empty blocks when the strip held back
      // a whole chunk as a partial marker.
      withContent = withContent.map((m) =>
        m.id === aiId ? { ...m, content: (m.content ?? '') + visibleText } : m,
      );
      const withBlocks =
        visibleText.length > 0
          ? appendBlock(withContent, aiId, {
              kind: 'text',
              text: visibleText,
              ...(chunk.agentId ? { agentId: chunk.agentId } : {}),
              ...(chunk.sourceMessageUuid ? { sourceMessageUuid: chunk.sourceMessageUuid } : {}),
            })
          : withContent;
      return {
        state: {
          ...tracked,
          pendingMarkers: nextPending,
          messages: withBlocks,
          // #3021 — authoritative content is landing; the transient preview is
          // superseded by the complete message. Clear it (no-op on the local
          // path, which never streams `preview`).
          livePreview: null,
        },
        effects: [],
      };
    }

    case 'thinking': {
      const aiId = tracked.currentAiMsgId;
      if (!aiId || !chunk.thinking) return { state: tracked, effects: [] };
      // Reasoning blocks land in `blocks` ONLY — never in `content`
      // (which is the answer text persisted to chat-history.json /
      // projected to the wire shape). Keeping thinking out of `content`
      // means a left-and-returned session doesn't resurrect reasoning
      // as answer text, and the legacy fallback render is unaffected.
      const thinkingText = redactSecrets(chunk.thinking);
      return {
        state: {
          ...tracked,
          messages: appendBlock(tracked.messages, aiId, {
            kind: 'thinking',
            text: thinkingText,
            ...(chunk.agentId ? { agentId: chunk.agentId } : {}),
            ...(chunk.sourceMessageUuid ? { sourceMessageUuid: chunk.sourceMessageUuid } : {}),
          }),
          // #3021 — authoritative reasoning is landing; clear the transient
          // preview it supersedes (no-op on the local path).
          livePreview: null,
        },
        effects: [],
      };
    }

    case 'tool_call': {
      const aiId = tracked.currentAiMsgId;
      if (!aiId || !chunk.toolCall) return { state: tracked, effects: [] };
      const tc = chunk.toolCall;
      // Upsert into the flat `toolCalls[]` (the live status/output store)
      // AND drop an ordered `tool_call` ref block so the card renders at
      // the right point in the interleave. Both de-dup on the call id.
      const withToolCall = tracked.messages.map((m) => {
        if (m.id !== aiId) return m;
        const existing = (m.toolCalls ?? []).find((t) => t.id === tc.id);
        if (existing) return m; // upsert: don't double-add
        return {
          ...m,
          toolCalls: [
            ...(m.toolCalls ?? []),
            {
              id: tc.id,
              name: tc.name,
              input: tc.input,
              status: 'running' as const,
              ...(chunk.sourceMessageUuid ? { sourceMessageUuid: chunk.sourceMessageUuid } : {}),
            },
          ],
        };
      });
      return {
        state: {
          ...tracked,
          messages: appendBlock(withToolCall, aiId, {
            kind: 'tool_call',
            toolCallId: tc.id,
            ...(chunk.agentId ? { agentId: chunk.agentId } : {}),
            ...(chunk.sourceMessageUuid ? { sourceMessageUuid: chunk.sourceMessageUuid } : {}),
          }),
          // #3021 — the complete message's tool_use is landing; clear the
          // transient preview it supersedes (no-op on the local path).
          livePreview: null,
        },
        effects: [],
      };
    }

    case 'tool_result': {
      const aiId = tracked.currentAiMsgId;
      if (!aiId || !chunk.toolResult) return { state: tracked, effects: [] };
      const tr = chunk.toolResult;
      const activeTurnId = tracked.messages.find((m) => m.id === aiId)?.turnId;
      const updated = updateToolCallById(tracked.messages, tr.id, activeTurnId, (t) => ({
        ...t,
        status: tr.isError ? ('error' as const) : ('success' as const),
        output: tr.output,
        ...(chunk.sourceMessageUuid
          ? { toolResultSourceMessageUuid: chunk.sourceMessageUuid }
          : {}),
        // Issue #529 — the `policy_decision` side-channel
        // (emitted by agent-adapter on every gate deny)
        // typically lands BEFORE this `tool_result` and
        // has already stamped `policyKind` on the entry.
        // If the tool_result chunk itself happens to
        // carry the kind (defensive: future SDK ordering
        // changes, IPC re-shuffles), prefer it; otherwise
        // keep whatever the side-channel set.
        ...(tr.policyKind !== undefined ? { policyKind: tr.policyKind } : {}),
      }));
      if (updated.found) {
        return {
          state: {
            ...tracked,
            messages: updated.messages,
          },
          effects: [],
        };
      }
      return {
        state: {
          ...tracked,
          messages: tracked.messages.map((m) => {
            if (m.id !== aiId) return m;
            return {
              ...m,
              toolCalls: (m.toolCalls ?? []).map((t) =>
                t.id === tr.id
                  ? {
                      ...t,
                      status: tr.isError ? ('error' as const) : ('success' as const),
                      output: tr.output,
                      ...(chunk.sourceMessageUuid
                        ? { toolResultSourceMessageUuid: chunk.sourceMessageUuid }
                        : {}),
                      // Issue #529 — the `policy_decision` side-channel
                      // (emitted by agent-adapter on every gate deny)
                      // typically lands BEFORE this `tool_result` and
                      // has already stamped `policyKind` on the entry.
                      // If the tool_result chunk itself happens to
                      // carry the kind (defensive: future SDK ordering
                      // changes, IPC re-shuffles), prefer it; otherwise
                      // keep whatever the side-channel set.
                      ...(tr.policyKind !== undefined ? { policyKind: tr.policyKind } : {}),
                    }
                  : t,
              ),
            };
          }),
        },
        effects: [],
      };
    }

    // Issue #529 — denial-classification side-channel from
    // agent-adapter. Emitted on every permission-gate deny BEFORE the
    // SDK's synthesised `tool_result` chunk for the denied call
    // lands. Tags the matching `ToolCallEntry` so the chip renderer
    // can branch on `policyKind === 'routed'` (subdued info chip)
    // vs `'blocked'` / undefined (existing red error chip).
    //
    // When no matching toolCall entry exists yet (pathological
    // ordering: chunk arrived before the `tool_call`), we no-op
    // rather than synthesise an entry. The matching tool_result will
    // create-and-tag in one step via its own branch above. This is
    // defensive: today the agent-adapter emits tool_call first, then
    // policy_decision, then tool_result, so the lookup always finds
    // a hit on the live path.
    case 'policy_decision': {
      const aiId = tracked.currentAiMsgId;
      if (!aiId || !chunk.policyDecision) return { state: tracked, effects: [] };
      const pd = chunk.policyDecision;
      const activeTurnId = tracked.messages.find((m) => m.id === aiId)?.turnId;
      const updated = updateToolCallById(tracked.messages, pd.toolUseID, activeTurnId, (t) => ({
        ...t,
        policyKind: pd.kind,
      }));
      if (updated.found) {
        return {
          state: {
            ...tracked,
            messages: updated.messages,
          },
          effects: [],
        };
      }
      return {
        state: {
          ...tracked,
          messages: tracked.messages.map((m) => {
            if (m.id !== aiId) return m;
            const toolCalls = m.toolCalls ?? [];
            // No-op when the tool call isn't mounted yet — the
            // tool_result branch above also accepts the kind on the
            // toolResult payload so the second-best ordering still
            // works.
            if (!toolCalls.some((t) => t.id === pd.toolUseID)) return m;
            return {
              ...m,
              toolCalls: toolCalls.map((t) =>
                t.id === pd.toolUseID ? { ...t, policyKind: pd.kind } : t,
              ),
            };
          }),
        },
        effects: [],
      };
    }

    case 'done': {
      // Phase 3 visibility — capture per-turn cost/turn-count off the
      // SDK's result message so the chat-header strip can render them.
      // Both fields are optional on the chunk; when absent we leave
      // lastTurnSpend at whatever it was (which is `null` after
      // startTurn cleared it, so the strip just shows nothing).
      const flushed = flushPendingMarker(tracked);
      return drainAfterTerminal({
        ...flushed,
        currentAiMsgId: null,
        isAiTyping: false,
        lastTurnSpend:
          chunk.totalCostUsd !== undefined || chunk.numTurns !== undefined
            ? {
                ...(chunk.totalCostUsd !== undefined ? { totalCostUsd: chunk.totalCostUsd } : {}),
                ...(chunk.numTurns !== undefined ? { numTurns: chunk.numTurns } : {}),
              }
            : tracked.lastTurnSpend,
      });
    }

    case 'interrupted': {
      // Append a soft "(stopped)" marker to the active assistant
      // message before clearing state. Distinct from the error
      // path: no red bubble — the user pressed Stop, this is an
      // intentional turn-end. Note: drain does NOT fire after
      // interrupted because Stop also clears the queue (see
      // `user/stop`); even if a queue is present from some other
      // path, we don't want to auto-send after a manual Stop.
      const flushed = flushPendingMarker(tracked);
      const aiId = flushed.currentAiMsgId;
      const messages = aiId
        ? flushed.messages.map((m) =>
            m.id === aiId ? { ...m, content: (m.content ?? '') + '\n\n_(stopped)_' } : m,
          )
        : flushed.messages;
      return {
        state: {
          ...flushed,
          messages,
          currentAiMsgId: null,
          isAiTyping: false,
          // #3021 — terminal reset (interrupted bypasses drainAfterTerminal);
          // null out the transient preview so a stopped turn leaves none behind.
          livePreview: null,
          // Stop also drops the queue (the action handler does this
          // before the chunk arrives, but defensive belt-and-braces
          // here too in case `interrupted` ever fires without a
          // preceding `user/stop`).
          queuedMessages: [],
        },
        effects: [],
      };
    }

    case 'error': {
      // Terminal SDK error — the iterator has unwound (or is about
      // to). Stamp the structured error onto the active assistant
      // bubble (or append a fresh error bubble if none active),
      // flip typing off, and drain any queued message. The
      // non-terminal counterpart is the `warning` chunk type.
      const errorPayload = unflattenIpcError(chunk.error ?? 'Unknown error');
      const flushed = flushPendingMarker(tracked);
      const aiId = flushed.currentAiMsgId;
      const messages = aiId
        ? flushed.messages.map((m) => (m.id === aiId ? { ...m, error: errorPayload } : m))
        : [
            ...flushed.messages,
            {
              id: `error-${Date.now()}`,
              role: 'assistant' as const,
              content: '',
              timestamp: Date.now(),
              error: errorPayload,
            },
          ];
      return drainAfterTerminal({
        ...flushed,
        messages,
        currentAiMsgId: null,
        isAiTyping: false,
        // Phase 3 visibility — capture spend off a terminal error chunk
        // too. The SDK reports `total_cost_usd` / `num_turns` on
        // result-error messages just like result-success, and the
        // user paid for that work so the strip should reflect it.
        lastTurnSpend:
          chunk.totalCostUsd !== undefined || chunk.numTurns !== undefined
            ? {
                ...(chunk.totalCostUsd !== undefined ? { totalCostUsd: chunk.totalCostUsd } : {}),
                ...(chunk.numTurns !== undefined ? { numTurns: chunk.numTurns } : {}),
              }
            : tracked.lastTurnSpend,
      });
    }

    case 'warning': {
      // Non-terminal SDK error / informational surface. Pre-PR-#171
      // this was an `'error'` chunk with a `terminal: false` flag;
      // splitting into a dedicated chunk type keeps the contract
      // explicit at the wire and removes the boolean-flag footgun
      // (it took alpha.42 → alpha.43 to ship the flag-aware
      // renderer guard the first time).
      //
      // Render the error inline on the active bubble but keep
      // `isAiTyping=true` and `currentAiMsgId` set so subsequent
      // text/tool chunks for the same logical assistant message
      // land on the right target.
      const errorPayload = unflattenIpcError(chunk.error ?? 'Unknown warning');
      const aiId = tracked.currentAiMsgId;
      const messages = aiId
        ? tracked.messages.map((m) => (m.id === aiId ? { ...m, error: errorPayload } : m))
        : [
            ...tracked.messages,
            {
              id: `warning-${Date.now()}`,
              role: 'assistant' as const,
              content: '',
              timestamp: Date.now(),
              error: errorPayload,
            },
          ];
      return { state: { ...tracked, messages }, effects: [] };
    }
  }
}

/**
 * Pop the head of the queue and start a new turn if non-empty.
 * Pure: returns the new state + effects. Called from the `done` and
 * terminal `error` branches of the chunk reducer.
 */
function drainAfterTerminal(state: ChatState): ReducerResult {
  // #3021 — the funnel for every terminal-reset seam (done / terminal error /
  // flight-reconciled / send-failed). Null out the transient preview here so a
  // finished turn never leaves stale in-progress motion behind.
  const cleared = state.livePreview === null ? state : { ...state, livePreview: null };
  if (cleared.queuedMessages.length === 0) {
    return { state: cleared, effects: [] };
  }
  const [head, ...rest] = cleared.queuedMessages;
  if (head === undefined) return { state: cleared, effects: [] };
  return startTurn({ ...cleared, queuedMessages: rest }, head.text, head.attachments);
}

// ---------------------------------------------------------------------------
// Phase 1 of the Agents-panel work — agents-map fold for chunk reducer
// ---------------------------------------------------------------------------

/**
 * Update the agents map in response to a single chunk:
 *
 *   - `agent_started` → insert a new row with status 'running'.
 *   - `agent_stopped` → flip the row's status to the stop reason +
 *     stamp `stoppedAt`. No-op if the row isn't present (race or
 *     stale chunk after a reset — drop silently).
 *   - any other chunk with a populated `agentId` → append to that
 *     agent's `recentChunks`, capped at MAX_RECENT_CHUNKS_PER_AGENT.
 *     If the agent isn't on the map, drop silently — likely a stale
 *     chunk arriving after the next turn started.
 *   - any other chunk → return state unchanged.
 *
 * Pure: returns a new state when the agents map changes, the same
 * state reference otherwise. Lives outside `reduceChunk` so the
 * unit tests can assert agents-map transitions in isolation.
 */
function applyAgentTracking(state: ChatState, chunk: AgentStreamChunk): ChatState {
  if (chunk.type === 'agent_started') {
    const info = chunk.subagent;
    if (!info) return state;
    const next = new Map(state.agents);
    next.set(info.agentId, {
      agentId: info.agentId,
      agentType: info.agentType,
      status: 'running',
      recentChunks: [],
      chunksSeenTotal: 0,
      ...(info.parentAgentId ? { parentAgentId: info.parentAgentId } : {}),
      startedAt: Date.now(),
    });
    return { ...state, agents: next };
  }
  if (chunk.type === 'agent_stopped') {
    const info = chunk.subagent;
    if (!info) return state;
    const existing = state.agents.get(info.agentId);
    if (!existing) return state;
    const next = new Map(state.agents);
    next.set(info.agentId, {
      ...existing,
      status: info.stopReason ?? 'success',
      stoppedAt: Date.now(),
    });
    return { ...state, agents: next };
  }
  if (chunk.agentId) {
    const existing = state.agents.get(chunk.agentId);
    if (!existing) return state;
    const appended = [...existing.recentChunks, chunk];
    const trimmed =
      appended.length > MAX_RECENT_CHUNKS_PER_AGENT
        ? appended.slice(appended.length - MAX_RECENT_CHUNKS_PER_AGENT)
        : appended;
    const next = new Map(state.agents);
    // `chunksSeenTotal` increments on EVERY append, including those
    // that get trimmed off the front. The renderer derives a stable
    // per-chunk key as `chunksSeenTotal - recentChunks.length + i`,
    // so a chunk's key never changes once assigned — even after the
    // cap kicks in and slices the front off.
    next.set(chunk.agentId, {
      ...existing,
      recentChunks: trimmed,
      chunksSeenTotal: existing.chunksSeenTotal + 1,
    });
    return { ...state, agents: next };
  }
  // #202 — un-tagged chunks land on the synthetic main-agent row.
  // Only the four passthrough types (text, tool_call, tool_result,
  // warning) get appended; terminal chunks (done, interrupted,
  // error) are handled by the per-type switch in `reduceChunk` and
  // the post-pass `withMainAgentStatus` recomputes status after
  // `isAiTyping` flips.
  //
  // Issue #529: `policy_decision` is a side-channel that mutates an
  // existing tool_call entry in place — it doesn't represent
  // independent activity on the main-agent timeline, so it
  // deliberately does NOT land on the recent-chunks list. The
  // existing `tool_call` / `tool_result` entries for the same id
  // carry the user-visible signal.
  if (
    chunk.type === 'text' ||
    chunk.type === 'thinking' ||
    chunk.type === 'tool_call' ||
    chunk.type === 'tool_result' ||
    chunk.type === 'warning'
  ) {
    return appendToMainAgent(state, chunk);
  }
  return state;
}

/**
 * Append an un-tagged passthrough chunk to the synthetic main-agent
 * row. Lazily creates the row on first un-tagged chunk of a turn
 * (same pattern as specialists' `agent_started` insert). Caps
 * `recentChunks` at `MAX_RECENT_CHUNKS_PER_AGENT` — front-trim,
 * matching specialist behaviour for memory-cap fairness.
 *
 * Status defaults to `'thinking'`; the post-pass
 * `withMainAgentStatus` reconciles it against `isAiTyping` and the
 * recent-chunk shape.
 */
function appendToMainAgent(state: ChatState, chunk: AgentStreamChunk): ChatState {
  const existing = state.mainAgent;
  const prevChunks = existing?.recentChunks ?? [];
  const appended = [...prevChunks, chunk];
  const trimmed =
    appended.length > MAX_RECENT_CHUNKS_PER_AGENT
      ? appended.slice(appended.length - MAX_RECENT_CHUNKS_PER_AGENT)
      : appended;
  return {
    ...state,
    mainAgent: {
      // Provisional status — final value is computed by
      // `withMainAgentStatus` once the per-type switch has settled
      // `isAiTyping` for terminal chunks.
      status: existing?.status ?? 'thinking',
      recentChunks: trimmed,
      chunksSeenTotal: (existing?.chunksSeenTotal ?? 0) + 1,
    },
  };
}

/**
 * Compute `mainAgent.status` from the current state.
 *
 *   - `idle`     when !isAiTyping
 *   - `working`  when isAiTyping AND most-recent chunk in
 *                recentChunks is a tool_call or tool_result
 *   - `thinking` otherwise (assistant generating text or thinking
 *                pre-tool)
 *
 * No-op when `mainAgent === null` — there's nothing to compute and
 * we don't want to materialise a row out of thin air.
 */
function computeMainAgentStatus(row: MainAgentRow, isAiTyping: boolean): MainAgentRow['status'] {
  if (!isAiTyping) return 'idle';
  // Walk recentChunks from the end — the freshest tool_call /
  // tool_result wins. Skip non-tool chunks (text, warning) so that
  // a tool that's still streaming results stays in 'working' even
  // if a status text chunk slipped in afterwards.
  for (let i = row.recentChunks.length - 1; i >= 0; i--) {
    const c = row.recentChunks[i];
    if (c?.type === 'tool_call' || c?.type === 'tool_result') {
      return 'working';
    }
  }
  return 'thinking';
}

/**
 * Reconcile `mainAgent.status` against the post-reduce-chunk state.
 * Called as the final pass in `reduceChunk` so terminal chunks
 * (`done`, `interrupted`, `error`) — which flip `isAiTyping` to
 * false — settle the row's status to `'idle'` even though they
 * never appended to `recentChunks` themselves.
 *
 * Pure: returns the same state reference when `mainAgent === null`
 * or the status is already correct, a new state otherwise.
 */
function withMainAgentStatus(state: ChatState): ChatState {
  if (!state.mainAgent) return state;
  const next = computeMainAgentStatus(state.mainAgent, state.isAiTyping);
  if (next === state.mainAgent.status) return state;
  return { ...state, mainAgent: { ...state.mainAgent, status: next } };
}
